/**
 * The goalkeeper. (§4.5)
 *
 * "This deserves its own section because it's usually the weakest part of indie
 * football games." The brief is right, and the reason is almost always the same:
 * the keeper is written as an oracle. It knows where the ball will be, it moves
 * there instantly, and the striker's only counter is to find the one animation
 * hole. That is not a goalkeeper, it is a wall with a hitbox bug.
 *
 * Every design choice below exists to make this keeper a PLAYER — one that reads
 * the shot, commits, and can be wrong:
 *
 *  1. REACTION IS A CLOCK STARTED BY THE STRIKER, NOT BY THE KEEPER.
 *     The keeper may not commit to a dive until `reactionTicks(Reflexes)` have
 *     elapsed since `ball.lastTouchTick` — the tick the ball was struck. Nothing
 *     else gates it. This has three properties we want and could not get any
 *     other way:
 *       - A close-range shot arrives before the clock expires. The keeper is
 *         beaten and does not so much as twitch. That is the correct outcome and
 *         it is the whole point of §4.5's 80–220ms.
 *       - A DEFLECTION updates lastTouchTick, so the clock RESTARTS. Deflected
 *         shots beat keepers here for exactly the reason they do in real life.
 *       - It needs no per-keeper timer field, so it survives clone/rewind for
 *         free. (§2.4, world.ts)
 *
 *  2. THE KEEPER'S PREDICTION IS DELIBERATELY WRONG ABOUT CURL.
 *     predictThreat() integrates gravity + drag but NOT Magnus (§4.5 permits
 *     this). Since Magnus is "the soul of the game" (§3.2), a keeper that
 *     modelled it would read a curling free-kick perfectly. Omitting it means
 *     bent shots beat keepers. The approximation IS the game design.
 *
 *  3. FULL-STRETCH SAVES ARE PARRIED, SET SAVES ARE CAUGHT.
 *     Catch chance scales down with the keeper's own speed at contact, so the
 *     spectacular saves spill and produce rebounds. See resolveSave().
 *
 *  4. HE DISTRIBUTES WITH HIS HANDS AS WELL AS HIS BOOT, AND HE CHOOSES.
 *     Owner report, verbatim: *"the goal keeper not only kicks he can also throw
 *     the ball"*. Three distributions (§4.5 "throw (short/long), drop-kick,
 *     punt"), each with its own release, trajectory and pose:
 *
 *       rollBallOut()      underarm, along the grass, to a near defender who is
 *                          FREE               → AnimState.GkRollOut
 *       throwBallOverarm() one arm, mid-range, flat lofted solve
 *                                             → AnimState.GkThrow
 *       kickBall()         drop-kick / punt, for distance or under pressure
 *                                             → AnimState.GkKick
 *
 *     `chooseDistribution()` picks between them on football grounds — pressure
 *     first, then whether a safe near option is open, then range — never at
 *     random and never on a single distance test. Both hand trajectories already
 *     existed inside one `throwBall`; what did not exist was a reason to prefer
 *     one, or a second pose, which is why every distribution looked like a kick.
 *
 *
 * ── State: where a keeper's memory lives ────────────────────────────────────
 *
 * WorldState is frozen (§15.2) and there is no `gkDiveTarget` field. There must
 * not be a module-scope `Map<PlayerId, DiveState>` either — it would not clone,
 * would not hash, and would desync reconciliation in exactly the way world.ts
 * warns about. So every bit of keeper memory is encoded in fields that already
 * clone and hash:
 *
 *   - reaction        → derived from ball.lastTouchTick (see 1 above)
 *   - "am I diving?"  → actionTimer > 0 && horizontal speed ≥
 *                       GK_DIVE_ACTIVE_MIN_SPEED. It CANNOT be read from the anim:
 *                       player-controller's stepAnim (stage 4) overwrites p.anim
 *                       from velocity every tick, keeper included, so a GkDive
 *                       pose is gone before we run at stage 5. The dive is
 *                       identified by its committed MOMENTUM instead — a lunging
 *                       keeper carries dive pace, a recovering one is stationary.
 *                       This was the live-match "keeper does nothing" bug: the old
 *                       anim test never fired through the full pipeline, so the
 *                       dive never advanced. (see gk-save-rate.test.ts)
 *   - dive target     → NOT stored. A dive is a committed straight-line lunge at
 *                       full dive pace: at commit we set vx/vy/vz and never
 *                       re-decide. Because stage 4 bleeds |v| off every tick,
 *                       integrateDive RE-ASSERTS that pace along the surviving
 *                       heading each tick — the direction survives, the magnitude
 *                       does not. A keeper still cannot course-correct mid-air.
 *   - hold + 6s rule  → gkHoldStartTick (the field exists for exactly this, §5.5)
 *   - recovery        → actionTimer > 0 && (near-)stationary (dive momentum gone)
 *
 * ── Cross-module contracts this file assumes (see the report) ───────────────
 *
 *   - Stage 4 (player-controller) runs over the keeper too: it decrements his
 *     actionTimer, bleeds his velocity toward a stop, applies gravity + a ground
 *     snap, and overwrites his anim. The dive is written to be immune to all four
 *     — momentum-based state detection, per-tick velocity re-assertion, a flat
 *     lunge, and anim re-stamping. The net of the double actionTimer decrement
 *     (stage 4 + our own) and the double position integrate (moveViaPhysics +
 *     integrateDive) is a dive that covers ~GK_DIVE_REACH, the intended envelope.
 *   - We run at pipeline stage 5, AFTER player control (4) and BEFORE kick
 *     resolution (6). That order is load-bearing twice over: we get the last
 *     word on the GK's anim, and a keeper's distribution clears possession
 *     before resolveKicks() can double-strike the same ball.
 */

import {
  type InputCommand,
  type PlayerArrays,
  type StepResult,
  type WorldState,
  AnimState,
  InputFlags,
  MatchPhase,
  MAX_PLAYERS,
} from './types.js';
import type { MatchConfig } from './types.js';
import {
  ATTRIBUTE_MAX,
  ATTRIBUTE_MIN,
  ATTRIBUTE_NEUTRAL,
  BALL_AREA,
  BALL_MASS,
  BALL_RADIUS,
  BALL_RESTITUTION_GRASS,
  CD_HIGH_SPEED,
  CD_LAMINAR,
  CD_TURBULENT,
  DRAG_CRISIS_MAX_SPEED,
  DRAG_LAMINAR_MAX_SPEED,
  DRAG_TURBULENT_MAX_SPEED,
  DRIVEN_PASS_MAX_SPEED,
  DRIVEN_PASS_MIN_SPEED,
  FIXED_DT,
  GK_AI_SURVEY_MAX_S,
  GK_AI_SURVEY_MIN_S,
  GK_DIVE_ACTIVE_MIN_SPEED,
  GK_DIVE_REACH,
  GK_DIVE_RECOVERY_S,
  GK_DIVE_SPEED,
  GK_RELEASE_ANIM_HOLD_S,
  GK_STEP_SPEED_THRESHOLD,
  GK_PARRY_OUTWARD_BIAS,
  GK_PARRY_SPEED_RETENTION,
  GK_PRESSURE_RADIUS,
  GK_OWN_TOUCH_REFRACTORY_S,
  GK_REACTION_MAX_MS,
  GK_REACTION_MIN_MS,
  GK_HOLD_LIMIT_S,
  GK_HAND_PASS_MAX_M,
  GK_HAND_PASS_CONE_RAD,
  GK_DROP_TAP_WINDOW_S,
  GK_SWEEPER_TRIGGER_DISTANCE,
  GK_SMOTHER_MAX_HEIGHT,
  GK_SMOTHER_MAX_RANGE,
  GK_THROW_MAX_RANGE_M,
  GOAL_AREA_LENGTH,
  GOAL_AREA_WIDTH,
  GOAL_HEIGHT,
  GOAL_WIDTH,
  GRAVITY,
  GROUND_PASS_MAX_SPEED,
  GROUND_PASS_MIN_SPEED,
  LOFTED_PASS_MAX_ANGLE_DEG,
  LOFTED_PASS_MIN_ANGLE_DEG,
  MAX_SPEED_JOG,
  MAX_SPEED_SPRINT,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
  PLAYER_CAPSULE_HEIGHT,
  PLAYER_CAPSULE_RADIUS,
  POSSESSION_RADIUS,
  SHOT_MAX_SPEED,
  SHOT_MIN_SPEED,
  SIMULATION_TICK_RATE,
  STAT_EFFECT_RANGE,
  WET_RESTITUTION_MULTIPLIER,
} from './constants.js';
import { type PitchBounds, pitchBounds } from './physics.js';
import {
  DEG2RAD,
  clamp,
  clamp01,
  datan2,
  dcos,
  dsin,
  dsqrt,
  lerp,
  remapClamped,
  angleDelta,
} from '../math/vec3.js';
import { pcg32Float } from '../math/pcg32.js';
import { BallVelocityCause, traceBallSince } from './ball-trace.js';
import { setAnimState } from './anim-exec.js';
import { gkTravelDistance, gkTravelX, gkTravelZ, recordGkTravel } from './gk-travel.js';
import { locomotionAnim } from './player-controller.js';
import { ContactSocket, computeSocket, type SocketVec } from './contact-sockets.js';

// ---------------------------------------------------------------------------
// GK tunables
//
// ✅ PROMOTED to constants.ts (2026-07-25) — the five feel parameters that used
// to live here as local `const`s (GK_DIVE_RECOVERY_S, GK_PARRY_SPEED_RETENTION,
// GK_AI_SURVEY_MIN_S/MAX_S, GK_THROW_MAX_RANGE_M) are now imported, per §15.5.
// The old note said "PROMOTE THESE WHEN THE FREEZE LIFTS"; it has lifted.
// Everything else below is DERIVED from an existing constant — algebra, not
// taste — and stays here where the algebra is readable.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Derived constants — no new tunables, just algebra over constants.ts
// ---------------------------------------------------------------------------

/** A dive lasts exactly as long as it takes to cover the reach at dive speed. */
const GK_DIVE_DURATION_S = GK_DIVE_REACH / GK_DIVE_SPEED;

/**
 * The dive clock is decremented BOTH by us (stage 5) and by player-controller's
 * advanceTimers (stage 4), so in the live pipeline it falls ~2 ticks per tick.
 * End the dive as soon as it is within a couple of ticks of zero rather than
 * waiting for an exact 0 it may overshoot — otherwise the last diving tick lands
 * on actionTimer already clamped to 0 and the keeper drops straight back into
 * normal AI with no recovery. Derived, not a new feel knob.
 */
const GK_DIVE_END_EPS = FIXED_DT * 2.5;

/** Square of the mid-dive momentum gate, precomputed. (§4.5) */
const GK_DIVE_ACTIVE_MIN_SPEED_SQ = GK_DIVE_ACTIVE_MIN_SPEED * GK_DIVE_ACTIVE_MIN_SPEED;

/**
 * The own-touch refractory in ticks. Ceil, so the window is never SHORTER than
 * the constant intends at any tick rate.
 */
const GK_OWN_TOUCH_REFRACTORY_TICKS =
  Math.ceil(GK_OWN_TOUCH_REFRACTORY_S * SIMULATION_TICK_RATE);

/**
 * ⚠ THE FLOOR THE ANTI-RE-GATHER GUARD IS SIZED AGAINST. Read this before
 * changing any release speed in this file.
 *
 * `resolveSave()` declines the keeper's OWN last touch for
 * GK_OWN_TOUCH_REFRACTORY_S, and that window is only long enough because the
 * SLOWEST release this file can produce still clears the claim envelope inside
 * it. GROUND_PASS_MIN_SPEED (8 m/s) is that slowest release — the short
 * roll-out — and every release path is floored at it (`rollBallOut` remaps up
 * from it; `throwBallOverarm` and `kickBall` are strictly faster). Verified by
 * gk-distribution.test.ts, which asserts both the algebra AND the measured
 * post-release ball speed.
 *
 * A ROLLED ball is the slowest thing a keeper puts on the grass, so this is
 * exactly where the re-gather bug would come back. Do not introduce a release
 * below this floor without re-sizing GK_OWN_TOUCH_REFRACTORY_S with it.
 */
const GK_RELEASE_MIN_SPEED = GROUND_PASS_MIN_SPEED;

/**
 * How long the release POSE is held so the renderer has something to play.
 *
 * Derived, and re-uses the constant that already exists for the same job:
 * THROW_ANIM_HOLD_S is "how long the thrower holds the throw pose". A keeper's
 * follow-through is the same beat.
 *
 * ⚠ It is NOT `actionTimer`, and that is load-bearing. stepKeeper reads
 * `actionTimer > 0` as "committed: mid-dive or recovering from one", and the
 * recovering branch puts the keeper in AnimState.Fallen — so arming the action
 * clock to hold a throw pose would lay the keeper on the floor after every
 * distribution. The hold is DERIVED from ball.lastTouchedBy/lastTouchTick
 * instead (the same two fields the refractory guard reads), which clone, hash
 * and survive rewind for free.
 */
/** The gather itself: GkCatchStanding is 1.00 s. Beyond it he is CARRYING. */
const GK_CATCH_ANIM_TICKS = Math.ceil(1.0 * SIMULATION_TICK_RATE);
const GK_RELEASE_ANIM_HOLD_TICKS = Math.ceil(GK_RELEASE_ANIM_HOLD_S * SIMULATION_TICK_RATE);

/**
 * Furthest a keeper will BOWL a ball out along the deck before the throw has to
 * go overarm. Derived, not a new tunable: PENALTY_AREA_LENGTH is where the
 * centre-back who has dropped to receive actually stands, and rolling it to the
 * man on the edge of his own box is literally what the short throw is for.
 */
const GK_ROLL_OUT_MAX_M = PENALTY_AREA_LENGTH;

/**
 * Which distribution the keeper is playing. Not on the wire and not stored —
 * `chooseDistribution()` recomputes it from geometry every time, so there is no
 * hidden per-keeper state to clone or hash (§15.2, world.ts).
 */
const enum GkDistribution {
  /** Bowled underarm along the grass to a free man close by. */
  RollOut = 0,
  /** Overarm, mid-range. */
  Throw = 1,
  /** The boot: drop-kick or punt, for distance or under pressure. */
  Punt = 2,
}

/** Positive gravity magnitude, for the ballistic solves. GRAVITY is signed. */
const G = -GRAVITY;

/**
 * The keeper's hands sit at his body centre; the whole reach envelope is
 * measured from there, not from his feet.
 */
const GK_HAND_HEIGHT = PLAYER_CAPSULE_HEIGHT * 0.5;
/** Scratch for the held-ball socket. Module scope = zero allocation (§15.7). */
const heldSocket: SocketVec = { x: 0, y: 0, z: 0 };

/** Back against the line — his capsule just touches it. (§4.5 positioning) */
const GK_MIN_DEPTH = PLAYER_CAPSULE_RADIUS;

/**
 * Furthest off his line in normal angle-play: the edge of the 6-yard box.
 * Coming further is sweeper-keeper work, gated separately. Real keepers live in
 * this band and the constant already exists for the right reason.
 */
const GK_MAX_DEPTH = GOAL_AREA_LENGTH;

const EPS = 1e-6;

// ---------------------------------------------------------------------------
// Pitch bounds cache
//
// pitchBounds() allocates, and §15.7 forbids allocation in the tick. This memo
// is NOT simulation state: it is a pure function of config, which world.ts
// guarantees is static for the match, and it re-derives whenever the pitch size
// differs from what it holds. So it cannot desync — two worlds with different
// pitch sizes alternating would thrash it and still both read correct values.
// ---------------------------------------------------------------------------

const boundsCache: PitchBounds = { halfLength: 0, halfWidth: 0, goalLineX: 0, touchlineZ: 0 };
let boundsCacheLength = Number.NaN;
let boundsCacheWidth = Number.NaN;

function getBounds(config: MatchConfig): PitchBounds {
  if (config.pitchLength !== boundsCacheLength || config.pitchWidth !== boundsCacheWidth) {
    const b = pitchBounds(config);
    boundsCache.halfLength = b.halfLength;
    boundsCache.halfWidth = b.halfWidth;
    boundsCache.goalLineX = b.goalLineX;
    boundsCache.touchlineZ = b.touchlineZ;
    boundsCacheLength = config.pitchLength;
    boundsCacheWidth = config.pitchWidth;
  }
  return boundsCache;
}

// ---------------------------------------------------------------------------
// Module-scope scratch. Reused every tick; never allocated in the loop. (§15.7)
// ---------------------------------------------------------------------------

/** Output of predictThreat(). */
const threat = {
  /** Did the predicted path cross the goal plane between the posts? */
  onTarget: false,
  /** Is there a point on that path the keeper can physically get to? */
  interceptable: false,
  /** Seconds from now to the earliest interceptable point. */
  t: 0,
  ix: 0,
  iy: 0,
  iz: 0,
};

/** Output of computeBisectorTarget(). */
const standPos = { x: 0, z: 0 };

/** Output of pickDistribution(): the best BALL, wherever it is on the pitch. */
const distTarget = { id: -1, x: 0, z: 0, dist: 0, space: 0 };

/**
 * Second output of pickDistribution(): the best SAFE SHORT option — the nearest
 * man inside roll-out range who is genuinely free. Separate from `distTarget`
 * because they answer different questions and a keeper weighs both: `distTarget`
 * is "where is the best ball on the pitch", `nearOption` is "can I just build
 * from the back". Scoring them together (as one `upfield + space` score) is what
 * made every distribution kick-shaped — the most advanced man with any room at
 * all always won, and the roll-out was unreachable in practice.
 */
const nearOption = { id: -1, x: 0, z: 0, dist: 0, space: 0 };

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/**
 * §8.1's balance constraint: any single stat may move a sim parameter by at
 * most ±STAT_EFFECT_RANGE. A great player with a bad avatar must still beat a
 * bad player with a great avatar, or the friends who play less quit.
 */
function statMul(stat: number): number {
  const n = clamp(stat, ATTRIBUTE_MIN, ATTRIBUTE_MAX);
  return 1 + STAT_EFFECT_RANGE * ((n - ATTRIBUTE_NEUTRAL) / (ATTRIBUTE_MAX - ATTRIBUTE_NEUTRAL));
}

/**
 * Reflexes → reaction latency in TICKS. (§4.5)
 *
 * NOTE the deliberate rule break: this maps across the FULL 80–220ms range, a
 * 2.75× spread, which blows straight through §8.1's ±15% cap that statMul()
 * enforces everywhere else in this file. §4.5 names both endpoints explicitly
 * and constants.ts exists to hold them, so the specific spec beats the general
 * one. Flagged rather than silently split the difference.
 */
function gkReactionTicks(reflexes: number): number {
  const ms = remapClamped(reflexes, ATTRIBUTE_MIN, ATTRIBUTE_MAX, GK_REACTION_MAX_MS, GK_REACTION_MIN_MS);
  return Math.ceil((ms / 1000) * SIMULATION_TICK_RATE);
}

/**
 * §3.2's drag crisis. Duplicated from ball.ts's model rather than imported:
 * ball.ts is owned by another agent and does not exist yet, and the keeper's
 * predictor is an approximation by design anyway (no Magnus). Exact agreement
 * with the real integrator is neither achievable nor wanted here.
 */
function dragCoefficient(speed: number): number {
  if (speed < DRAG_LAMINAR_MAX_SPEED) return CD_LAMINAR;
  if (speed < DRAG_CRISIS_MAX_SPEED) {
    // The crisis itself: Cd collapses as the boundary layer trips turbulent.
    const f = (speed - DRAG_LAMINAR_MAX_SPEED) / (DRAG_CRISIS_MAX_SPEED - DRAG_LAMINAR_MAX_SPEED);
    return lerp(CD_LAMINAR, CD_TURBULENT, f);
  }
  if (speed < DRAG_TURBULENT_MAX_SPEED) return CD_TURBULENT;
  return CD_HIGH_SPEED;
}

/**
 * Which way this team attacks. The keeper defends the goal BEHIND him, so his
 * goal line is on the opposite side to his attacking direction.
 */
function attacksPositiveX(state: WorldState, team: number): boolean {
  return (team === 0) === state.teamAAttacksPositiveX;
}

function setAnim(p: PlayerArrays, id: number, anim: AnimState): void {
  setAnimState(p, id, anim);
}

function advanceAnim(p: PlayerArrays, id: number, durationS: number): void {
  p.animBlend[id] = clamp01(p.animBlend[id]! + FIXED_DT / durationS);
}

/** The three poses a keeper's DISTRIBUTION can leave him in. (§4.5) */
function isGkReleaseAnim(anim: number): boolean {
  return anim === AnimState.GkRollOut || anim === AnimState.GkThrow || anim === AnimState.GkKick;
}

/**
 * Is this keeper inside the follow-through of his OWN distribution?
 *
 * Derived from ball.lastTouchedBy/lastTouchTick — the same two already-hashed
 * fields the anti-re-gather guard in resolveSave() reads, so it clones, hashes
 * and survives a reconciliation rewind for free, with no new WorldState field
 * and nothing on the wire. (§2.4, world.ts, and the file header on where keeper
 * memory lives.)
 */
function inReleaseFollowThrough(state: WorldState, id: number): boolean {
  const ball = state.ball;
  return (
    ball.lastTouchedBy === id &&
    ball.possessedBy !== id &&
    state.tick - ball.lastTouchTick < GK_RELEASE_ANIM_HOLD_TICKS
  );
}

/**
 * The keeper's default stance — his set position — EXCEPT while a distribution
 * he has just played is still following through, when the release pose keeps the
 * frame instead.
 *
 * ⚠ ONE HALF OF A PAIR — the other half is stepAnim's `isHandReleaseAnim`
 * branch in player-controller.ts, and NEITHER IS SUFFICIENT ALONE.
 *
 * Stage 4's stepAnim re-derives p.anim from velocity for every player every tick
 * (that is the documented reason reassertDiveAnim has to exist at all), so a
 * release pose set at stage 5 survives its own tick and is wiped at stage 4 of
 * the NEXT one — a 16.7 ms flash, shorter than one render cross-fade, which is
 * exactly what "there is no animation" looks like. stepAnim now HOLDS a hand
 * release for THROW_ANIM_HOLD_S; this function is the stage-5 half that stops the
 * keeper stamping GkIdle straight back over the pose stage 4 just preserved.
 *
 * Both read the same derived window, so they cannot disagree: ball.lastTouchedBy
 * / lastTouchTick, the two already-hashed fields resolveSave's anti-re-gather
 * guard also reads.
 *
 * NOT `actionTimer`: see GK_RELEASE_ANIM_HOLD_TICKS. Arming the action clock to
 * hold a pose would route the keeper into stepKeeper's recovery branch and lay
 * him on the floor in AnimState.Fallen after every distribution.
 */
function setStanceAnim(state: WorldState, id: number): void {
  const p = state.players;
  if (inReleaseFollowThrough(state, id) && isGkReleaseAnim(p.anim[id]!)) {
    advanceAnim(p, id, GK_RELEASE_ANIM_HOLD_S);
    return;
  }

  // ── A MOVING KEEPER IS NOT IN HIS STANCE ─────────────────────────────────
  //
  // `GkIdle` binds to the sustained pose `GkReady` in the renderer, held at FULL
  // weight with the gait underneath at weight ZERO — a pose, i.e. a planted
  // foot. Stamping it unconditionally is what made every keeper reposition a
  // 100% skate: MEASURED at 45.7 m of ground slid under a planted foot per three
  // minutes of match, 99.0% of all the time a keeper spent above walking pace.
  //
  // The travel comes from gk-travel.ts rather than from |v| because his movers
  // zero his velocity on purpose (see that file, and moveToward's header) — |v|
  // is exactly the quantity that cannot see this, which is why the counter that
  // was watching for it read zero for months.
  //
  // ⚠ It defers to `locomotionAnim`, the SAME ladder every outfielder uses,
  // instead of picking a gait here. A second ladder is how the renderer and the
  // sim came to disagree about backward travel, and a keeper shuffling across
  // his line while facing the ball is a STRAFE — which that function already
  // decides correctly from facing vs. travel, and which a bespoke speed-only
  // branch here would have got wrong.
  //
  // ⚠ TWO SOURCES, AND USING ONLY ONE LEAVES 94% OF THE DEFECT BEHIND.
  // A keeper is moved in two completely different ways and both end up here:
  //
  //   - POSITION-FIRST (`moveToward`, `applyStageTargets`): velocity is zeroed
  //     on purpose, so |v| reads 0 and only gk-travel.ts knows he moved.
  //   - ORDINARY LOCOMOTION (stage 4), which owns him whenever he is not being
  //     swept or staged — most visibly when he is the GOAL-KICK TAKER walking
  //     out to the ball. There |v| is honest, `stepAnim` has ALREADY chosen the
  //     right clip at stage 4, and this function's job is simply not to trample
  //     it. gk-travel records nothing for that case, correctly.
  //
  // Fixing only the first left the second untouched: MEASURED, the residual was
  // 131 of 140 surviving stance-while-travelling samples, all goal kicks, all
  // with recorded travel 0.000 while the real speed climbed 1.500, 1.650, 1.800
  // — increments of exactly ACCELERATION, the signature of stage-4 locomotion.
  //
  // So take whichever mover actually moved him. Below WALK_SPEED_THRESHOLD he
  // really is set, and the stance is the truth.
  const travelSpeed = gkTravelDistance(state, id) / FIXED_DT;
  const ownSpeed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
  const usePositionFirst = travelSpeed >= ownSpeed;
  const speed = usePositionFirst ? travelSpeed : ownSpeed;
  // ⚠ THE KEEPER'S GATE, NOT THE OUTFIELD ONE. WALK_SPEED_THRESHOLD (1.4) is
  // where an outfielder starts walking; a keeper adjusting his line lives
  // BELOW it, and `GkIdle` is a planted pose — so that gate left every
  // shuffle a statue gliding sideways (measured: 118 of 118 moving ticks).
  if (speed > GK_STEP_SPEED_THRESHOLD) {
    setAnim(p, id, locomotionAnim(
      p, id,
      usePositionFirst ? gkTravelX(state, id) / FIXED_DT : p.vx[id]!,
      usePositionFirst ? gkTravelZ(state, id) / FIXED_DT : p.vz[id]!,
      speed,
      p.anim[id]!,
    ));
    return;
  }

  setAnim(p, id, AnimState.GkIdle);
}

/**
 * Facing yaw → forward direction.
 *
 * ⚠ CONVENTION ASSUMPTION: forward = (cos yaw, 0, sin yaw), i.e. yaw =
 * datan2(dz, dx). vec3.ts names datan2 "the aim/facing primitive" and this is
 * its direct XZ reading. player-controller.ts owns the real convention and does
 * not exist yet. Every yaw↔direction conversion in this file goes through here
 * and yawForwardZ(), so reconciling is a two-line change, not an audit.
 */
function yawForwardX(yaw: number): number {
  return dcos(yaw);
}

function yawForwardZ(yaw: number): number {
  return dsin(yaw);
}

/** Is the keeper inside his own penalty area, i.e. allowed to use his hands? */
function insideOwnBox(x: number, z: number, goalX: number, sideSign: number, bounds: PitchBounds): boolean {
  // Depth measured from the goal line into the pitch.
  const depth = -sideSign * (x - goalX);
  return depth >= 0 && depth <= PENALTY_AREA_LENGTH && Math.abs(z) <= PENALTY_AREA_WIDTH * 0.5
    && Math.abs(x) <= bounds.halfLength;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Advance every goalkeeper one tick. Called from step.ts stage 5.
 *
 * A keeper is any player whose `positions[id] === 'GK'`. Nothing else marks one
 * — there is no isKeeper flag and there should not be, because §9.2 makes squad
 * size a config and a 2v2 keeper is the same code as an 11v11 keeper.
 */
export function stepGoalkeeper(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
): void {
  const bounds = getBounds(state.config);
  for (let id = 0; id < MAX_PLAYERS; id++) {
    if (state.positions[id] !== 'GK') continue;
    if (state.players.active[id] !== 1) continue;
    if (state.players.sentOff[id] === 1) continue;
    stepKeeper(state, id, inputs[id] ?? null, out, bounds);
  }
}

// ---------------------------------------------------------------------------
// Per-keeper tick
// ---------------------------------------------------------------------------

function stepKeeper(
  state: WorldState,
  id: number,
  input: InputCommand | null,
  out: StepResult,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const attrs = state.attributes[id]!;
  const team = p.team[id]!;

  // Geometry of the goal this keeper defends. He attacks one way, so his goal
  // is behind him at the other end. sideSign points from the centre spot toward
  // his own goal line; forwardX is up the pitch.
  const sideSign = attacksPositiveX(state, team) ? -1 : 1;
  const goalX = sideSign * bounds.goalLineX;
  const forwardX = -sideSign;

  // We own this decrement — see the header note on the actionTimer split.
  if (p.actionTimer[id]! > 0) {
    p.actionTimer[id] = Math.max(0, p.actionTimer[id]! - FIXED_DT);
  }

  const live = state.match.phase === MatchPhase.InPlay;

  // ── Holding the ball ────────────────────────────────────────────────────
  if (p.gkHoldStartTick[id]! >= 0) {
    if (!live) {
      // A restart was awarded while he had it (goal kick, free kick, half-time).
      // rules/ owns the ball from here; let go cleanly rather than fight it.
      releaseHold(state, id);
    } else {
      stepHeldBall(state, id, input, out, attrs, goalX, forwardX, bounds);
      return;
    }
  }

  // ── A BALL AT HIS FEET IN HIS OWN BOX GETS PICKED UP, HERE ───────────────
  //
  // Owner, live report: "when ball is kicked near the goal keeper he slides
  // with the ball till near the net and throws".
  //
  // REPRODUCED (sim/gk-hold-probe.test.ts): a ball arriving under the capture
  // gate never triggers a hand save, so `updatePossession` gives it to him as
  // ORDINARY FOOT POSSESSION. `stepHeldBall` never runs, nothing plants him,
  // and he is simply a player with the ball — so the keeper AI walked him
  // BACKWARDS toward his own goal before gathering. Measured: carried 5.56 m,
  // finishing 0.30 m off his own goal line, and only picking it up 3.2 s later.
  // That travel is the "slide", and the pick-up at the end is the "throws".
  //
  // A real keeper picks up a ball rolled to him where he stands. `resolveSave`
  // already owns every legality question — inside his own box, the back-pass
  // rule, and the exemption for his own last touch — so this offers it the
  // ball rather than duplicating any of that. If it declines (a team-mate's
  // deliberate pass, or his own clearance a moment ago) he keeps it at his
  // feet and plays it as an outfield player, which is correct.
  if (live && state.ball.possessedBy === id && p.gkHoldStartTick[id]! < 0
      && p.actionTimer[id]! <= 0) {
    if (resolveSave(state, id, input, attrs, goalX, sideSign, forwardX, bounds)
        && p.gkHoldStartTick[id]! >= 0) {
      return; // gathered — stepHeldBall plants him from the next tick
    }
  }

  // ── Committed: mid-dive, or recovering after one ─────────────────────────
  //
  // ⚠ THE BUG THIS BLOCK EXISTS TO SURVIVE (§4.5, the live-match "keeper does
  // nothing" report). The keeper's committed state was read from `p.anim`
  // (anim ∈ {GkDiveLeft, GkDiveRight} ⇒ "I am diving"). That is fine in
  // isolation — the __gk_smoke harness calls ONLY stepGoalkeeper and it works —
  // but step.ts stage 4 runs player-controller's stepAnim() over EVERY active
  // player, the keeper included, and stepAnim OVERWRITES `p.anim` from the
  // keeper's velocity every tick. So by the time we run at stage 5 the GkDive
  // pose is already gone, this test could never see it, integrateDive() never
  // ran, and the keeper re-committed a fresh micro-dive every tick without ever
  // travelling — 0% of real shots produced a save attempt that moved him.
  //
  // The fix reads the commitment from state stage 4 CANNOT erase: the action
  // clock plus MOMENTUM. A lunging keeper still carries dive pace while his clock
  // runs; a punch/fallen keeper is stationary (their handlers zero him), and a
  // keeper merely repositioning has no clock. We re-assert the dive pose (for the
  // renderer) and the dive velocity (which stepLocomotion bleeds off) as we go.
  const diveSpeed = GK_DIVE_SPEED * statMul(attrs.diving);
  if (p.actionTimer[id]! > 0) {
    const hSpeedSq = p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!;
    if (hSpeedSq >= GK_DIVE_ACTIVE_MIN_SPEED_SQ) {
      // Mid-dive. integrateDive re-asserts the committed dive pace against the
      // stage-4 bleed and advances him; he can still get a hand to it in flight.
      integrateDive(state, id, diveSpeed);
      reassertDiveAnim(p, id, sideSign);
      advanceAnim(p, id, GK_DIVE_DURATION_S);
      if (live) resolveSave(state, id, input, attrs, goalX, sideSign, forwardX, bounds);
      // Near the end of the (double-decremented) clock, tip into the ground
      // recovery so he cannot instantly re-contest a rebound. A lunge is flat, so
      // there is no airborne tail to wait out.
      if (p.actionTimer[id]! <= GK_DIVE_END_EPS) endDive(state, id);
      return;
    }
    // Recovering: fallen after a dive, or the palm-away follow-through of a
    // punch/parry. Planted — he cannot contest the rebound, which is the price of
    // committing. Serve the clock out.
    p.vx[id] = 0;
    p.vy[id] = 0;
    p.vz[id] = 0;
    setAnim(p, id, AnimState.Fallen);
    advanceAnim(p, id, GK_DIVE_RECOVERY_S);
    // ⚠ A BODY ON THE GROUND STILL STOPS WHAT REACHES IT. The dive's doubled
    // motion (see integrateDive) arrives at the intercept in HALF the time
    // predictThreat promised, so on placed shots the keeper reached the spot,
    // fell, and then lay there as a GHOST while the ball passed within claim
    // radius of his hands — traced tick by tick 2026-08-14: dive ends t=0.47,
    // ball arrives t=0.63 at 0.92 m and nothing was allowed to touch it.
    // Corner save rate measured 2 % (gk-save-rate CORNER-PROBE: 0 contacts in
    // 209 attempted corner saves, half passing inside 1.2 m). A keeper who
    // beat the ball to the spot GATHERS it — that is most of what arriving
    // early is FOR. Guarded to balls still on the play side of his line, so a
    // scored goal is never fished back out of the net between tick and
    // whistle. He still cannot chase a rebound - his velocity stays zeroed.
    if (live && sideSign * (state.ball.px - goalX) < 0) {
      resolveSave(state, id, input, attrs, goalX, sideSign, forwardX, bounds);
    }
    return;
  }

  if (!live) {
    // Restarts position their own takers. Stand still rather than shuffle into
    // rules/'s carefully placed penalty-taker geometry. (setStanceAnim, not a
    // bare GkIdle, so a punt that put the ball out of play still shows its
    // follow-through across the phase flip.)
    setStanceAnim(state, id);
    return;
  }

  // ── Anything in reach gets claimed, human or AI ──────────────────────────
  if (resolveSave(state, id, input, attrs, goalX, sideSign, forwardX, bounds)) return;

  if (input !== null) {
    stepHumanKeeper(state, id, input, attrs, sideSign);
    return;
  }

  stepAiKeeper(state, id, attrs, goalX, sideSign, forwardX, bounds);
}

// ---------------------------------------------------------------------------
// Holding the ball: the 6-second rule (§5.5) and distribution (§4.5)
// ---------------------------------------------------------------------------

function releaseHold(state: WorldState, id: number): void {
  state.players.gkHoldStartTick[id] = -1;
  state.players.possessing[id] = 0;
  if (state.ball.possessedBy === id) state.ball.possessedBy = -1;
}

function stepHeldBall(
  state: WorldState,
  id: number,
  input: InputCommand | null,
  out: StepResult,
  attrs: WorldState['attributes'][number],
  goalX: number,
  forwardX: number,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const ball = state.ball;

  // Re-assert the hold every tick. updatePossession() ran at stage 3 and may
  // have handed possession elsewhere; while the ball is in his gloves it is his.
  ball.possessedBy = id;
  p.possessing[id] = 1;

  // Glue it to his HANDS so no other module sees a loose ball — and to his
  // hands, not to the middle of his capsule. This said "glue it to his hands"
  // and then computed PLAYER_CAPSULE_RADIUS forward at PLAYER_CAPSULE_HEIGHT ×
  // 0.5, which is his WAIST. Rendered close-up (MODELS_SHOWCASE 1100): the ball
  // floating at belt height in front of a keeper whose arms hang at his sides.
  // The owner asked "make sure he really hold the ball and pick up"; that
  // picture is the answer being no.
  //
  // ⚠ STILL OWED: this places the ball where the gloves ARE in the gather pose,
  // it does not PARENT it to the animated hand bone. A ball that is socket-
  // placed still cannot follow the hands through the release. That is a
  // renderer change (the cast ships hand sockets) and it is recorded as owed
  // rather than pretended away.
  computeSocket(state, id, ContactSocket.Hands, heldSocket);
  ball.px = heldSocket.x;
  ball.pz = heldSocket.z;
  ball.py = heldSocket.y;
  const gvx0 = ball.vx, gvy0 = ball.vy, gvz0 = ball.vz;
  ball.vx = 0; ball.vy = 0; ball.vz = 0;
  traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, gvx0, gvy0, gvz0);
  ball.wx = 0; ball.wy = 0; ball.wz = 0;
  ball.grounded = 0;
  p.vx[id] = 0;
  p.vz[id] = 0;

  const heldTicks = state.tick - p.gkHoldStartTick[id]!;
  // ⚠ THE GATHER IS A ONE-SHOT; CARRYING IS NOT. This stamped `GkCatch` on
  // every held tick, and GkCatch is a 1.0 s one-shot — so a keeper who has
  // gathered and is now carrying the ball stood frozen in the clip's last
  // frame until he released it (owner: "not carrying the ball"). The catch
  // covers the gather; after it he holds his READY stance, which is a keeper
  // with the ball in his gloves looking for the out-ball. He is stationary
  // while held (vx/vz are zeroed just above), so the stance is the truth.
  if (heldTicks < GK_CATCH_ANIM_TICKS) {
    setAnim(p, id, AnimState.GkCatch);
  } else {
    setAnim(p, id, AnimState.GkIdle);
  }

  // Law 12. Fire on the EXACT tick the threshold is crossed: gkHoldStartTick
  // only changes on grab/release and tick advances by one, so `===` fires once
  // and only once. A `>=` here would spam an event every tick until rules/
  // happened to change phase.
  const holdLimitTicks = Math.ceil(GK_HOLD_LIMIT_S * SIMULATION_TICK_RATE);
  if (heldTicks === holdLimitTicks) {
    out.events.push({
      type: 'SIX_SECOND',
      tick: state.tick,
      team: 1 - p.team[id]!,
      player: id,
      x: p.px[id]!,
      z: p.pz[id]!,
    });
  }

  // ── Human: distribute on the normal pass/shoot verbs ─────────────────────
  if (input !== null) {
    const flags = input.flags;
    const dirX = yawForwardX(input.aimYaw);
    const dirZ = yawForwardZ(input.aimYaw);

    // ── X+X — PUT IT DOWN (owner's scheme) ─────────────────────────────────
    //
    // "to drop the ball its x+ x / then he will place the ball on ground and
    // the goal keeper can kick or pass".
    //
    // A double tap rather than a single one because dropping is IRREVERSIBLE:
    // Law 12 forbids him picking it up again, so a stray press in his own
    // six-yard box would be a turnover. Two taps inside GK_DROP_TAP_WINDOW_S
    // is a deliberate act; one is a fumble of the keyboard.
    //
    // He places it at his feet and the hold ends. From there he IS an outfield
    // player — the ordinary kick and pass paths own him, which is exactly what
    // the owner asked for and needs no new distribution mode.
    if ((flags & InputFlags.GkHold) !== 0) {
      if (p.gkHoldTapPrev[id] === 0) {
        const since = state.tick - p.gkHoldTapTick[id]!;
        p.gkHoldTapTick[id] = state.tick;
        p.gkHoldTapPrev[id] = 1;
        if (p.gkHoldTapTick[id]! >= 0 && since <= GK_DROP_TAP_WINDOW_S * SIMULATION_TICK_RATE) {
          placeBallAtFeet(state, id, dirX, dirZ);
          return;
        }
      }
    } else {
      p.gkHoldTapPrev[id] = 0;
    }

    // ── LEFT CLICK — THROW IT (owner: "when hold the boll left click to
    //    throw it"). Charge weights the distance, so a flick is a short throw
    //    to a full-back and a held click is the long one down the channel.
    if ((flags & InputFlags.Shoot) !== 0) {
      const power = clamp01(input.shootCharge);
      const range = lerp(PLAYER_CAPSULE_RADIUS, GK_THROW_MAX_RANGE_M, power);
      throwBall(state, id, attrs, dirX, dirZ, range);
      return;
    }

    // ── RIGHT CLICK — HAND IT TO A TEAM-MATE (owner: "right click to pass to
    //    team mate with hands"). Aim picks the man; the roll is weighted to
    //    REACH HIM rather than to a charge, because a hand-pass to a specific
    //    player is a distance you can measure, not one you guess at.
    if ((flags & InputFlags.Pass) !== 0) {
      const mate = nearestMateInAimCone(state, id, input.aimYaw);
      if (mate >= 0) {
        const mx = p.px[mate]! - p.px[id]!;
        const mz = p.pz[mate]! - p.pz[id]!;
        const md = Math.sqrt(mx * mx + mz * mz) || 1;
        throwBall(state, id, attrs, mx / md, mz / md, md);
      } else {
        // Nobody in the cone: it is still a hand-pass, just an unaimed one.
        const power = clamp01(input.passCharge);
        throwBall(state, id, attrs, dirX, dirZ,
          lerp(PLAYER_CAPSULE_RADIUS, GK_ROLL_OUT_MAX_M, power));
      }
      return;
    }
    return;
  }

  // ── AI: survey, then pick the best option ────────────────────────────────
  // Vision decides how fast he finds it. A poor keeper dithers toward the law's
  // limit; a good one is already looking as he catches it.
  const surveyS = remapClamped(
    attrs.vision, ATTRIBUTE_MIN, ATTRIBUTE_MAX, GK_AI_SURVEY_MAX_S, GK_AI_SURVEY_MIN_S,
  );
  if (heldTicks * FIXED_DT < surveyS) return;

  pickDistribution(state, id, goalX, forwardX, bounds);
  if (distTarget.id < 0 && nearOption.id < 0) {
    // Nobody on. Boot it upfield down the nearest touchline rather than roll it
    // to a striker — the one thing a keeper must never do.
    const outZ = p.pz[id]! >= 0 ? 1 : -1;
    kickBall(state, id, attrs, forwardX, outZ * 0.35, 1, 0);
    return;
  }

  const mode = chooseDistribution(state, id);
  // A roll-out goes to the near man; a throw or a punt goes to the best ball on
  // the pitch. chooseDistribution() only returns RollOut when nearOption exists.
  const target = mode === GkDistribution.RollOut ? nearOption : distTarget;
  if (target.id < 0) {
    const outZ = p.pz[id]! >= 0 ? 1 : -1;
    kickBall(state, id, attrs, forwardX, outZ * 0.35, 1, 0);
    return;
  }

  const dx = target.x - p.px[id]!;
  const dz = target.z - p.pz[id]!;
  const d = dsqrt(dx * dx + dz * dz);
  if (d < EPS) return;
  const dirX = dx / d;
  const dirZ = dz / d;

  switch (mode) {
    case GkDistribution.RollOut:
      rollBallOut(state, id, attrs, dirX, dirZ, d);
      return;
    case GkDistribution.Throw:
      throwBallOverarm(state, id, attrs, dirX, dirZ, d);
      return;
    default:
      kickBall(state, id, attrs, dirX, dirZ, 1, 0);
      return;
  }
}

/**
 * Distance from this keeper to the nearest opponent, or +∞ if there is none.
 * The one geometric fact both halves of the distribution decision turn on.
 */
function nearestOpponentDistance(state: WorldState, id: number): number {
  const p = state.players;
  const team = p.team[id]!;
  const gx = p.px[id]!, gz = p.pz[id]!;
  let best = Number.MAX_VALUE;
  for (let j = 0; j < MAX_PLAYERS; j++) {
    if (j === id) continue;
    if (p.active[j] !== 1 || p.sentOff[j] === 1) continue;
    if (p.team[j] === team) continue;
    const dx = p.px[j]! - gx;
    const dz = p.pz[j]! - gz;
    const d = dsqrt(dx * dx + dz * dz);
    if (d < best) best = d;
  }
  return best;
}

/**
 * THROW OR KICK? (§4.5 "distribution: throw (short/long), drop-kick, punt")
 *
 * Owner report, verbatim: *"the goal keeper not only kicks he can also throw the
 * ball"*. Both trajectories already existed; what did not exist was a REASON to
 * pick one, so `d <= GK_THROW_MAX_RANGE_M` decided everything and the target
 * selector (which scored `upfield + space`, i.e. "the most advanced man with any
 * room at all") pushed almost every ball past 32 m and out to the boot.
 *
 * The three things a real keeper weighs, in the order he weighs them:
 *
 *  1. AM I BEING CLOSED DOWN?  An opponent inside GK_PRESSURE_RADIUS ⇒ the boot,
 *     whatever else is on. You do not bend down to bowl a ball out, or wind up an
 *     overarm throw, with a striker arriving — that is how the ball ends up in
 *     the net. This branch is deliberately BLIND to distance: it is the "get rid
 *     of it" branch, and it is what makes the choice respond to pressure rather
 *     than only to geometry.
 *  2. IS THERE A SAFE NEAR OPTION?  A man inside GK_ROLL_OUT_MAX_M with at least
 *     GK_PRESSURE_RADIUS of grass around him ⇒ roll it to him. This is building
 *     from the back, it retains the ball, and it is the single most common thing
 *     a modern keeper does with it. Note the space bar is the SAME radius as the
 *     pressure bar above — one number, one question, asked about two players.
 *  3. HOW FAR IS THE BEST BALL?  Inside GK_THROW_MAX_RANGE_M the arm beats the
 *     boot (faster to release, flatter, more accurate) ⇒ overarm throw. Beyond
 *     it the arm cannot reach ⇒ punt.
 *
 * No RNG: this is pure geometry over positions that already clone and hash, so
 * it adds no draw to the shared stream and cannot desync a replay. (§15.3, D1)
 */
function chooseDistribution(state: WorldState, id: number): GkDistribution {
  // 1. Pressed.
  if (nearestOpponentDistance(state, id) <= GK_PRESSURE_RADIUS) {
    return GkDistribution.Punt;
  }
  // 2. A free man close by.
  if (nearOption.id >= 0) return GkDistribution.RollOut;
  // 3. Range decides between the arm and the boot.
  if (distTarget.id >= 0 && distTarget.dist <= GK_THROW_MAX_RANGE_M) {
    return GkDistribution.Throw;
  }
  return GkDistribution.Punt;
}

/**
 * Common release: the ball leaves the gloves and is live again.
 *
 * `handHeight` is the height the ball leaves from, measured off the keeper's
 * feet. It defaults to GK_HAND_HEIGHT (his body centre) for the throws and kicks
 * that leave from up there; the ROLL-OUT passes BALL_RADIUS instead, because a
 * bowled ball leaves the hand ON THE GRASS.
 *
 * ⚠ The low release is not cosmetic — it strictly WIDENS the anti-re-gather
 * margin. resolveSave() measures the ball from the HAND POINT (feet +
 * GK_HAND_HEIGHT), so a ball on the deck starts 0.79 m below the hands and needs
 * only ~0.60 m of travel to leave the 1.2 m claim envelope, against ~0.90 m for a
 * hand-height release. See the numbers on rollBallOut().
 */
function releaseBall(
  state: WorldState,
  id: number,
  vx: number, vy: number, vz: number,
  handHeight: number = GK_HAND_HEIGHT,
): void {
  const p = state.players;
  const ball = state.ball;
  const yaw = p.yaw[id]!;
  ball.px = p.px[id]! + yawForwardX(yaw) * PLAYER_CAPSULE_RADIUS;
  ball.pz = p.pz[id]! + yawForwardZ(yaw) * PLAYER_CAPSULE_RADIUS;
  ball.py = p.py[id]! + handHeight;
  const rlvx0 = ball.vx, rlvy0 = ball.vy, rlvz0 = ball.vz;
  ball.vx = vx; ball.vy = vy; ball.vz = vz;
  traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, rlvx0, rlvy0, rlvz0);
  ball.grounded = 0;
  ball.lastTouchedBy = id;
  ball.lastTouchedTeam = p.team[id]!;
  ball.lastTouchTick = state.tick;
  // Clear possession BEFORE resolveKicks() runs at stage 6, or it would see a
  // possessed ball plus a held Pass flag and strike the same ball twice.
  ball.possessedBy = -1;
  p.possessing[id] = 0;
  p.gkHoldStartTick[id] = -1;
}

/**
 * Arm strength, not boot power, sets a throw's ceiling.
 * Shared by both hand releases so they cannot drift apart.
 */
function maxThrowSpeed(attrs: WorldState['attributes'][number]): number {
  return GROUND_PASS_MAX_SPEED * statMul(attrs.strength);
}

/**
 * ROLL-OUT — bowled underarm along the deck. (§4.5 "throw (short)")
 *
 * The safe distribution: a keeper crouches, sweeps his arm along the ground and
 * rolls the ball to a defender who is free, so the man receives it already
 * moving and facing up the pitch. Released ON THE GRASS (`BALL_RADIUS`) with
 * zero vertical, which is both what a bowl looks like and what makes it roll
 * instead of drop.
 *
 * ⚠ RE-GATHER SAFETY — the number that matters, checked because a rolled ball is
 * the SLOWEST thing this file ever releases and is therefore the exact place the
 * "keeper catches his own punt" bug (GK_OWN_TOUCH_REFRACTORY_S) would return:
 *
 *   release point   0.3 m in front of the keeper's axis (PLAYER_CAPSULE_RADIUS),
 *                   0.79 m BELOW his hand point (GK_HAND_HEIGHT 0.9 − BALL_RADIUS
 *                   0.11) ⇒ 0.845 m from the hands already.
 *   claim envelope  POSSESSION_RADIUS = 1.2 m, measured from the hand point.
 *   travel needed   √(1.2² − 0.79²) − 0.3 = 0.603 m.
 *   slowest speed   GROUND_PASS_MIN_SPEED = 8 m/s (the `range → 0` end of the
 *                   remap below; `Math.min` with maxThrowSpeed cannot bite,
 *                   because the weakest possible keeper still has
 *                   24 × 0.85 = 20.4 m/s of arm).
 *   time to clear   0.603 / 8 = 0.0754 s = 4.5 ticks ⇒ 5 ticks.
 *   guard window    GK_OWN_TOUCH_REFRACTORY_S = 0.2 s = 12 ticks.
 *
 * 12 ticks against 5: the guard is 2.6× longer than the slowest roll needs, i.e.
 * MORE margin than the punt it was originally sized for (which needed 7). Drag
 * over 75 ms at 8 m/s costs ~0.12 m/s, and the ball is still airborne for the
 * whole of it (a 0.79 m drop takes 0.4 s), so rolling resistance cannot bite
 * either. No widening required. Asserted in gk-distribution.test.ts, both as
 * algebra and as a measured full-pipeline trace.
 */
function rollBallOut(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  dirX: number,
  dirZ: number,
  range: number,
): void {
  const p = state.players;
  p.yaw[id] = datan2(dirZ, dirX);
  // Speed scales with how far it has to travel, floored at GK_RELEASE_MIN_SPEED.
  const v = Math.min(
    maxThrowSpeed(attrs),
    remapClamped(range, 0, GK_ROLL_OUT_MAX_M, GK_RELEASE_MIN_SPEED, GROUND_PASS_MAX_SPEED),
  );
  releaseBall(state, id, dirX * v, 0, dirZ * v, BALL_RADIUS);
  setAnim(p, id, AnimState.GkRollOut);
  // ⚠ NOT actionTimer — see GK_RELEASE_ANIM_HOLD_TICKS. Arming the action clock
  // here would send him into stepKeeper's recovery branch and lay him on the
  // floor (AnimState.Fallen) after every roll-out.
  p.actionTimer[id] = 0;
}

/**
 * OVERARM THROW — one arm, above the shoulder, mid-range. (§4.5 "throw (long)")
 *
 * Ballistic solve for the launch speed that lands on the target:
 *   R = v²·sin(2θ)/g  ⇒  v = √(R·g / sin 2θ)
 * at the flattest lofted angle, because a throw's whole advantage over the boot
 * is that it arrives sooner and flatter.
 *
 * Always strictly faster than the roll-out at the same range (it must clear the
 * ground, so it needs more speed for the same carry), which is why the
 * re-gather margin computed on rollBallOut() bounds this path too.
 */
function throwBallOverarm(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  dirX: number,
  dirZ: number,
  range: number,
): void {
  const p = state.players;
  p.yaw[id] = datan2(dirZ, dirX);
  const theta = LOFTED_PASS_MIN_ANGLE_DEG * DEG2RAD;
  const s2 = dsin(2 * theta);
  const maxThrow = maxThrowSpeed(attrs);
  const solved = s2 > EPS ? dsqrt((range * G) / s2) : maxThrow;
  // Floored at the same release minimum the guard is sized against: a very short
  // overarm solve would otherwise drop below it. (See rollBallOut's numbers.)
  const v = clamp(solved, GK_RELEASE_MIN_SPEED, maxThrow);
  const vh = v * dcos(theta);
  releaseBall(state, id, dirX * vh, v * dsin(theta), dirZ * vh);
  setAnim(p, id, AnimState.GkThrow);
  p.actionTimer[id] = 0;
}

/**
 * Throw, range-dispatched — the entry a HUMAN keeper's Pass verb uses, so one
 * button gives him the roll-out up close and the overarm hurl further out
 * exactly the way the AI gets them. The split is GK_ROLL_OUT_MAX_M.
 */

/**
 * The team-mate a holding keeper is aiming at, or -1. Nearest inside a cone
 * about the aim, so pointing roughly at a man picks him without demanding
 * pixel accuracy on a moving target.
 */
function nearestMateInAimCone(state: WorldState, id: number, aimYaw: number): number {
  const p = state.players;
  const team = p.team[id]!;
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    const dx = p.px[i]! - p.px[id]!;
    const dz = p.pz[i]! - p.pz[id]!;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < 1e-3 || d > GK_HAND_PASS_MAX_M) continue;
    if (Math.abs(angleDelta(aimYaw, datan2(dz, dx))) > GK_HAND_PASS_CONE_RAD) continue;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

/**
 * X+X — place the ball on the ground at his feet and let go of it.
 *
 * ⚠ IRREVERSIBLE BY LAW. Once released a keeper may not handle it again until
 * someone else touches it, which is why this is a double tap. The hold ends
 * here and he becomes an ordinary carrier: `updatePossession` picks the ball
 * up at his feet next tick and the normal kick/pass paths own him.
 */
function placeBallAtFeet(state: WorldState, id: number, dirX: number, dirZ: number): void {
  const p = state.players;
  const ball = state.ball;
  ball.px = p.px[id]! + dirX * PLAYER_CAPSULE_RADIUS;
  ball.pz = p.pz[id]! + dirZ * PLAYER_CAPSULE_RADIUS;
  ball.py = BALL_RADIUS;
  const v0x = ball.vx, v0y = ball.vy, v0z = ball.vz;
  ball.vx = 0; ball.vy = 0; ball.vz = 0;
  ball.wx = 0; ball.wy = 0; ball.wz = 0;
  ball.grounded = 1;
  traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, v0x, v0y, v0z);
  releaseHold(state, id);
  setAnim(p, id, AnimState.GkPlaceBall);
}

function throwBall(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  dirX: number,
  dirZ: number,
  range: number,
): void {
  if (range <= GK_ROLL_OUT_MAX_M) {
    rollBallOut(state, id, attrs, dirX, dirZ, range);
  } else {
    throwBallOverarm(state, id, attrs, dirX, dirZ, range);
  }
}

/**
 * Kick — drop-kick or punt, decided by ballistics rather than a magic flag.
 *
 * Try the flat trajectory (LOFTED_PASS_MIN_ANGLE_DEG) first: flatter means the
 * ball arrives sooner, which is what you want when a counter is on. If the
 * keeper's leg cannot reach the target that flat, go up to
 * LOFTED_PASS_MAX_ANGLE_DEG, which is nearer the 45° range optimum. So near
 * targets get a driven drop-kick and far ones get a punt, for free.
 */
function kickBall(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  dirX: number,
  dirZ: number,
  power: number,
  liftBias: number,
): void {
  const p = state.players;
  const maxKick = remapClamped(
    attrs.kicking, ATTRIBUTE_MIN, ATTRIBUTE_MAX, DRIVEN_PASS_MIN_SPEED, DRIVEN_PASS_MAX_SPEED,
  ) * clamp01(power);

  // Mouse wheel still means "height" for a human keeper. (§4.1)
  const theta = clamp(
    lerp(LOFTED_PASS_MIN_ANGLE_DEG, LOFTED_PASS_MAX_ANGLE_DEG, clamp01(liftBias * 0.5 + 0.5)),
    LOFTED_PASS_MIN_ANGLE_DEG, LOFTED_PASS_MAX_ANGLE_DEG,
  ) * DEG2RAD;

  const len = dsqrt(dirX * dirX + dirZ * dirZ);
  const nx = len > EPS ? dirX / len : 0;
  const nz = len > EPS ? dirZ / len : 0;
  p.yaw[id] = datan2(nz, nx);

  const vh = maxKick * dcos(theta);
  releaseBall(state, id, nx * vh, maxKick * dsin(theta), nz * vh);
  setAnim(p, id, AnimState.GkKick);
  p.actionTimer[id] = 0;
}

/**
 * Pick who to distribute to — TWO answers, because a keeper asks two questions.
 *
 * `distTarget` = the best ball on the pitch. Scored in metres against metres,
 * deliberately: `upfield + space` needs no weighting constant and still says the
 * right thing — a man 20m up the pitch with an opponent 2m off him (22) loses to
 * a man 5m up with 15m of grass (20) only when the gap is genuinely worth it.
 * Tunable-free by construction.
 *
 * `nearOption` = the best SAFE SHORT ball: the NEAREST man inside
 * GK_ROLL_OUT_MAX_M who has at least GK_PRESSURE_RADIUS of grass around him.
 * Nearest rather than best-scored, because the point of a roll-out is that it is
 * the shortest, safest pass available, not the most ambitious one.
 *
 * ⚠ Why this needed a SECOND answer rather than a tweak to the score: `upfield`
 * runs to the length of the pitch while `space` saturates at a few metres, so
 * the single score is dominated by upfield and always returns the most advanced
 * man with any room at all. That man is normally past GK_THROW_MAX_RANGE_M, which
 * is how "the keeper only ever kicks" happened — the short options were never
 * even candidates. The `space > POSSESSION_RADIUS` bar (1.2 m) is also far too
 * low to roll a ball to somebody: 1.2 m of grass is an opponent already inside
 * touching distance.
 */
function pickDistribution(
  state: WorldState,
  id: number,
  goalX: number,
  forwardX: number,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const team = p.team[id]!;
  distTarget.id = -1;
  distTarget.space = 0;
  nearOption.id = -1;
  nearOption.space = 0;
  let bestScore = -Number.MAX_VALUE;
  let bestNearDist = Number.MAX_VALUE;

  for (let j = 0; j < MAX_PLAYERS; j++) {
    if (j === id) continue;
    if (p.active[j] !== 1 || p.sentOff[j] === 1) continue;
    if (p.team[j] !== team) continue;
    if (state.positions[j] === 'GK') continue;

    const tx = p.px[j]!;
    const tz = p.pz[j]!;
    const upfield = forwardX * (tx - goalX);
    if (upfield < 0) continue; // behind his own goal line

    // Nearest opponent to this teammate = how much room he has.
    let space = bounds.halfLength;
    for (let k = 0; k < MAX_PLAYERS; k++) {
      if (p.active[k] !== 1 || p.sentOff[k] === 1) continue;
      if (p.team[k] === team) continue;
      const ox = p.px[k]! - tx;
      const oz = p.pz[k]! - tz;
      const d = dsqrt(ox * ox + oz * oz);
      if (d < space) space = d;
    }
    // Never hand it to a man who is already being closed down.
    if (space <= POSSESSION_RADIUS) continue;

    const dx = tx - p.px[id]!;
    const dz = tz - p.pz[id]!;
    const dist = dsqrt(dx * dx + dz * dz);

    // ── The safe short ball: close, and genuinely FREE (not merely un-tackled).
    if (dist <= GK_ROLL_OUT_MAX_M && space >= GK_PRESSURE_RADIUS && dist < bestNearDist) {
      bestNearDist = dist;
      nearOption.id = j;
      nearOption.x = tx;
      nearOption.z = tz;
      nearOption.space = space;
      nearOption.dist = dist;
    }

    const score = upfield + space;
    if (score > bestScore) {
      bestScore = score;
      distTarget.id = j;
      distTarget.x = tx;
      distTarget.z = tz;
      distTarget.space = space;
      distTarget.dist = dist;
    }
  }
}

// ---------------------------------------------------------------------------
// Threat prediction (§4.5 "trigger when the ball's predicted trajectory
// intersects the goal plane within reach")
// ---------------------------------------------------------------------------

/**
 * Forward-integrate the ball and answer two questions in ONE pass:
 *   - will it cross the goal plane between the posts?          (is it a shot?)
 *   - what is the EARLIEST point on that path the keeper can physically get to?
 *
 * Gravity + drag only. No Magnus, by design — see the header. No allocation:
 * the ball is carried in scalar locals and the answer lands in `threat`.
 *
 * `reach(t) = min(diveReach, diveSpeed·t)` is the capsule sweep from §4.5,
 * expressed analytically. The swept volume of the keeper's hands moving at
 * diveSpeed from his position IS a capsule, so testing the predicted ball
 * against that radius is the same query — minus a WASM round-trip per tick, and
 * minus the physics handle that stepGoalkeeper()'s signature does not give us
 * anyway.
 *
 * Earliest, not latest: keepers save as early as they can reach, which is why
 * they come off their line at all.
 */
function predictThreat(
  state: WorldState,
  gx: number, gy: number, gz: number,
  goalX: number, sideSign: number,
  diveReach: number, diveSpeed: number,
  horizonTicks: number,
): void {
  const ball = state.ball;
  let bx = ball.px, by = ball.py, bz = ball.pz;
  let vx = ball.vx, vy = ball.vy, vz = ball.vz;

  threat.onTarget = false;
  threat.interceptable = false;
  threat.t = 0;

  const restitution = BALL_RESTITUTION_GRASS * (state.config.wetPitch ? WET_RESTITUTION_MULTIPLIER : 1);
  // a_drag = -(½·ρ·Cd·A/m)·|v|·v  (§3.2)
  const kDrag = (0.5 * state.config.airDensity * BALL_AREA) / BALL_MASS;

  for (let s = 1; s <= horizonTicks; s++) {
    const prevX = bx, prevY = by, prevZ = bz;

    const speed = dsqrt(vx * vx + vy * vy + vz * vz);
    if (speed > 0) {
      const k = kDrag * dragCoefficient(speed) * speed * FIXED_DT;
      vx -= vx * k; vy -= vy * k; vz -= vz * k;
    }
    vy += GRAVITY * FIXED_DT;
    bx += vx * FIXED_DT; by += vy * FIXED_DT; bz += vz * FIXED_DT;
    if (by < BALL_RADIUS) {
      by = BALL_RADIUS;
      if (vy < 0) vy = -vy * restitution;
    }

    const t = s * FIXED_DT;

    if (!threat.interceptable) {
      const dx = bx - gx, dy = by - gy, dz = bz - gz;
      const need = dsqrt(dx * dx + dy * dy + dz * dz);
      if (need <= Math.min(diveReach, diveSpeed * t)) {
        threat.interceptable = true;
        threat.t = t;
        threat.ix = bx; threat.iy = by; threat.iz = bz;
      }
    }

    // Goal-plane crossing. `d` is signed distance past the keeper's own goal
    // line, so it goes negative → positive exactly once, on the way in.
    const dPrev = sideSign * (prevX - goalX);
    const dCur = sideSign * (bx - goalX);
    if (dPrev < 0 && dCur >= 0) {
      const span = dCur - dPrev;
      const f = span === 0 ? 0 : -dPrev / span;
      const cy = prevY + (by - prevY) * f;
      const cz = prevZ + (bz - prevZ) * f;
      // Law 10's whole-ball test, the same way physics.ts states it: any part of
      // the ball inside the frame is on target.
      threat.onTarget =
        Math.abs(cz) <= GOAL_WIDTH * 0.5 + BALL_RADIUS &&
        cy <= GOAL_HEIGHT + BALL_RADIUS;
      return;
    }
  }
}

// ---------------------------------------------------------------------------
// Diving
// ---------------------------------------------------------------------------

/**
 * Launch. He commits everything to a straight-line lunge at full dive pace
 * toward the intercept's ground point `(tx,tz)`, plus a modest upward push if the
 * ball is genuinely above his hands. Committed direction, never re-steered — you
 * cannot change direction mid-air, and the no-target-field design (§4.5 header)
 * holds because the launch velocity IS the commitment.
 *
 * Why the horizontal is set to the FULL diveSpeed rather than solved for arrival
 * time: the dive must be RECONSTRUCTABLE every tick, because player-controller
 * bleeds the keeper's speed off at stage 4 (see integrateDive). A constant known
 * pace along a surviving direction can be re-asserted; a per-dive solved speed
 * cannot. The reach envelope is still honoured — the dive lasts exactly
 * GK_DIVE_DURATION_S, so it covers exactly GK_DIVE_REACH, and resolveSave()
 * claims the ball the instant the lunge carries a hand within reach of it.
 */
function commitDive(
  state: WorldState,
  id: number,
  tx: number, ty: number, tz: number,
  sideSign: number,
  diveSpeed: number,
): void {
  const p = state.players;

  let dx = tx - p.px[id]!;
  let dz = tz - p.pz[id]!;
  const dl = dsqrt(dx * dx + dz * dz);
  if (dl > EPS) {
    dx /= dl; dz /= dl;
  } else {
    // Ball coming straight down his own line — no lateral to lunge along. Pick a
    // side to fall to off a seeded flip, never a fixed one.
    dx = 0;
    dz = pcg32Float(state.rng) < 0.5 ? -1 : 1;
  }

  p.vx[id] = dx * diveSpeed;
  p.vz[id] = dz * diveSpeed;

  // ⚠ THE OVERSHOOT BUG THIS EXISTS TO KILL (§4.5, "he dont defend goal kick at
  // the very corner he dont follow ball"). This used to commit every dive for
  // the FULL GK_DIVE_DURATION_S regardless of `dl` (the distance to the
  // intercept just computed above) — so a target needing only, say, half his
  // reach still ran the lunge for the full budget and sailed straight past it
  // in the same fixed direction (never re-steered, by design). Measured on
  // gk-save-rate.test.ts's corner bucket: dive displacement averaged ~4.2-4.4m
  // against targets that only needed ~2.5-3m, landing him 1.3-1.9m WIDE of
  // where the ball actually was and producing 0/110 contacts on attempted,
  // unsaved corner dives despite predictThreat correctly identifying the
  // intercept point. The duration must be sized to the trip, not the budget —
  // same ratio GK_DIVE_DURATION_S itself uses (distance/diveSpeed), so a dive
  // that genuinely needs the full reach is completely unaffected (dl≈diveReach
  // ⇒ this reduces to the old constant), and anything shorter now actually
  // stops at the ball instead of blowing through it.
  // Floored at one tick: `dl` can be ~0 (ball landing essentially where he
  // already stands, or the straight-down-the-line case above, which never
  // reads `dl` for direction but still flows through this same formula) —
  // without the floor that divides `rise` below by zero.
  const diveDurationS = Math.max(FIXED_DT, Math.min(GK_DIVE_DURATION_S, dl / diveSpeed));

  // A modest upward push only for a ball above his hands, so a waist/head-high
  // save gets a real spring while a grounder stays flat and slides. Kept small:
  // the horizontal lunge is what covers the reach, and the vertical is fighting
  // gravity applied TWICE per tick (stage-4 moveViaPhysics + integrateDive), so a
  // big arc would be eaten anyway — the top corner staying hard to reach is right.
  const rise = ty - GK_HAND_HEIGHT - p.py[id]!;
  p.vy[id] = rise > 0 ? Math.min(rise / diveDurationS + 0.5 * G * diveDurationS, diveSpeed) : 0;

  p.actionTimer[id] = diveDurationS;

  // ── Dive, or SMOTHER? ──────────────────────────────────────────────────────
  //
  // Owner: *"like how real goal keeper do when they get the ball they sleep on
  // the ball"*. A ball ON THE DECK and close enough to fall on is not a
  // full-stretch lateral leap — it is dropping onto it and covering it. The
  // `GkSmother` clip for exactly that has SHIPPED, bound, since the GK pack
  // landed; it could never fire because every commitment here was tagged as a
  // dive, and `animationCommand.ts` listed GK_SMOTHER in UNREACHABLE_COMMANDS
  // with that as the stated reason. AnimState.GkSmother is the missing link
  // between an authored clip and a sim state that earns it.
  //
  // The two conditions are what make it a smother rather than a dive: the ball
  // is at boot height, and it is inside a body-length rather than out at full
  // stretch. Anything higher or wider stays a dive. (§4.5)
  const sdx = tx - p.px[id]!;
  const sdz = tz - p.pz[id]!;
  if (ty <= GK_SMOTHER_MAX_HEIGHT
    && sdx * sdx + sdz * sdz <= GK_SMOTHER_MAX_RANGE * GK_SMOTHER_MAX_RANGE) {
    setAnimState(p, id, AnimState.GkSmother);
  } else {
    reassertDiveAnim(p, id, sideSign);
  }
  p.yaw[id] = datan2(0, -sideSign);
}

/**
 * Re-stamp the dive pose. stepAnim (stage 4) overwrites p.anim from velocity
 * every tick, so the keeper's own dive clip has to be re-asserted at stage 5 or
 * the client renders him jogging through his own dive. Left/right is read from
 * the lunge direction in the keeper's frame — he always faces up the pitch, so
 * facing +X (sideSign −1 ⇒ forwardX +1) his right hand points +Z.
 */
function reassertDiveAnim(p: PlayerArrays, id: number, sideSign: number): void {
  // A smother is committed once and held for the whole action. Without this the
  // per-tick re-assert would overwrite it with a dive on the very next tick and
  // the clip would be unreachable all over again, one frame later.
  if (p.anim[id]! === AnimState.GkSmother) {
    setAnimState(p, id, AnimState.GkSmother);
    return;
  }
  const lateral = p.vz[id]! * -sideSign;
  // `setAnimState`, NOT a raw write: this runs EVERY tick of the dive. A raw
  // write stamps nothing (the value is unchanged) but a raw write plus a naive
  // per-write stamp would issue a new execution id sixty times per save, and the
  // director would restart the dive clip on every one of them. The change-guard
  // is what makes the whole dive ONE execution. (§11)
  setAnimState(p, id, lateral >= 0 ? AnimState.GkDiveRight : AnimState.GkDiveLeft);
}

/**
 * In flight, one tick. RE-ASSERTS the committed dive pace first, then advances.
 *
 * The re-assert is the whole point of the live-match fix: player-controller's
 * stepLocomotion (stage 4) sees the keeper's null AI input and decelerates him
 * toward a stop every tick, bleeding ~DECELERATION·dt off his |v| — left alone,
 * the lunge stalls a few ticks in and he never travels his reach. stepLocomotion
 * preserves the velocity DIRECTION though, so we restore the horizontal MAGNITUDE
 * to the committed diveSpeed along that surviving heading. Vertical is left to
 * gravity so an upward dive still arcs and falls.
 *
 * Touching down does NOT end the dive and must not zero his horizontal velocity:
 * a keeper who hits the deck keeps sliding into the ball, which is most of what a
 * low dive IS. Only the fall stops at ground level; the dive ends on the clock,
 * in stepKeeper.
 */
function integrateDive(state: WorldState, id: number, diveSpeed: number): void {
  const p = state.players;

  const hx = p.vx[id]!, hz = p.vz[id]!;
  const hl = dsqrt(hx * hx + hz * hz);
  if (hl > EPS) {
    const s = diveSpeed / hl;
    p.vx[id] = hx * s;
    p.vz[id] = hz * s;
  }

  // ⚠ NOT single-integrated, and that is deliberate scope, not an oversight.
  // fix/tackle-keeper's "one gravity, one integrator" pass (4f2fad8) removed
  // this double integration AND stopped stage-4 (player-controller.ts)
  // integrating the dive's horizontal a second time via a new `gkAuthored`
  // gate — the two halves were fixed together because GK_DIVE_REACH had been
  // tuned against the OLD doubled travel and de-coupling them measurably
  // collapsed the keeper (goal% 32% -> 59% on the 485-shot sweep) and
  // regressed `ai/tactical-metrics`'s presser cross-check for team 0
  // (89.9% -> 42.7%, measured) once `gkAuthored` also froze his normal
  // AI positioning walk. That AI-movement interaction is out of scope for a
  // ball-to-hand attachment fix, so only the smother-reachability half of
  // 4f2fad8 is taken here (AnimState.GkSmother + this function's branch to
  // it) and the dive's own motion is left exactly as this branch already
  // shipped it — double-integrated, same as every dive before this commit.
  p.vy[id] = p.vy[id]! + GRAVITY * FIXED_DT;
  p.px[id] = p.px[id]! + p.vx[id]! * FIXED_DT;
  p.py[id] = p.py[id]! + p.vy[id]! * FIXED_DT;
  p.pz[id] = p.pz[id]! + p.vz[id]! * FIXED_DT;

  if (p.py[id]! <= 0) {
    p.py[id] = 0;
    if (p.vy[id]! < 0) p.vy[id] = 0;
  }
}

/** He is down and out of time: put him on the floor to get back up. */
function endDive(state: WorldState, id: number): void {
  const p = state.players;
  p.py[id] = 0;
  p.vx[id] = 0; p.vy[id] = 0; p.vz[id] = 0;
  setAnim(p, id, AnimState.Fallen);
  p.actionTimer[id] = GK_DIVE_RECOVERY_S;
}

// ---------------------------------------------------------------------------
// Handling (§4.5): catch vs parry vs punch
// ---------------------------------------------------------------------------

/**
 * The ball is inside his hands' radius — decide what happens to it.
 * Returns true if he got a touch.
 */
function resolveSave(
  state: WorldState,
  id: number,
  input: InputCommand | null,
  attrs: WorldState['attributes'][number],
  goalX: number,
  sideSign: number,
  forwardX: number,
  bounds: PitchBounds,
): boolean {
  const p = state.players;
  const ball = state.ball;

  const gx = p.px[id]!, gz = p.pz[id]!;
  const gy = p.py[id]! + GK_HAND_HEIGHT;
  // ⚠ A KEEPER CAN BEND DOWN. The claim sphere is centred on his HANDS
  // (gy = py + GK_HAND_HEIGHT), so a ball sitting on the ground at his own feet
  // was ~1.4 m BELOW the centre of a 1.2 m sphere — outside it. The result was
  // that a keeper could never pick up a ball at his feet at all: it stayed
  // ordinary foot possession and he dribbled it, which is the owner's live
  // report "he slides with the ball till near the net and throws" (measured:
  // carried 5.56 m, finishing 0.30 m off his own goal line, gathered 3.2 s
  // later).
  //
  // A GROUNDED ball is therefore judged on horizontal reach — he stoops for it,
  // which is what a keeper does and what the gather clip already shows. An
  // AIRBORNE ball keeps the full 3D test against the hands, because reaching a
  // ball at head height genuinely is a different question.
  const dx = ball.px - gx, dy = ball.py - gy, dz = ball.pz - gz;
  const reach = ball.grounded === 1
    ? dsqrt(dx * dx + dz * dz)
    : dsqrt(dx * dx + dy * dy + dz * dz);
  if (reach > POSSESSION_RADIUS) return false;

  // Hands are only legal inside his own box. Outside it he is an outfield player
  // and player-controller.ts owns him. Handling out there is a red card, and
  // spotting that offence is rules/'s job, not ours.
  if (!insideOwnBox(gx, gz, goalX, sideSign, bounds)) return false;

  // Back-pass (§5.5). Picking this up concedes an indirect free kick, so he
  // plays it with his feet instead. Note we only decline to HANDLE it — whether
  // it was a deliberate pass, and therefore an offence, stays rules/'s call.
  if (ball.lastTouchedTeam === p.team[id]! && ball.lastTouchedBy !== id && ball.grounded === 1) {
    return false;
  }

  // ── His OWN last touch, moments ago: hands off. (Law 12, §4.5) ────────────
  //
  // ⚠ THE BUG THIS EXISTS TO KILL — the live report "goal keeper unable to kick
  // the ball, bugs out". releaseBall() plants the ball PLAYER_CAPSULE_RADIUS
  // (0.3 m) in front of his hands and the claim above reaches POSSESSION_RADIUS
  // (1.2 m), so on the tick AFTER he distributes, his own clearance is still
  // inside his own claim envelope — and the back-pass guard directly above
  // deliberately exempts his own touch (`lastTouchedBy !== id`), so nothing
  // declined it. The catch roll then picked a flavour of broken: caught ⇒ the
  // hold re-armed and he punted/caught forever, with the 8-second law never
  // firing because gkHoldStartTick reset each cycle; parried ⇒ parryBall aims at
  // the corner FLAG (through goalX), so he palmed his own punt back at his own
  // goal and then lay in Fallen recovery while it rolled goalwards. Measured
  // pre-fix: release@t143 → re-gather@t144, 537 held ticks, 0 SIX_SECOND.
  // (gk-distribution.test.ts)
  //
  // Law 12 is the same rule in legal form: having released the ball, he may not
  // touch it with the hands again before another player has. Anyone else
  // touching it moves lastTouchedBy off him and lifts this instantly, so the
  // window self-clears and can never wedge him — and it is short enough that
  // gathering a genuine rebound off his own parry is untouched.
  //
  // Derived, not stored: lastTouchedBy/lastTouchTick already clone and hash, so
  // this survives rewind for free exactly like the reaction gate (see header).
  if (
    ball.lastTouchedBy === id &&
    state.tick - ball.lastTouchTick < GK_OWN_TOUCH_REFRACTORY_TICKS
  ) {
    return false;
  }

  const speed = dsqrt(ball.vx * ball.vx + ball.vy * ball.vy + ball.vz * ball.vz);
  const gkSpeed = dsqrt(p.vx[id]! * p.vx[id]! + p.vy[id]! * p.vy[id]! + p.vz[id]! * p.vz[id]!);
  const diveSpeed = GK_DIVE_SPEED * statMul(attrs.diving);

  // ── Punch? ───────────────────────────────────────────────────────────────
  // Human: the modifier is held. AI: it is high and someone is climbing all over
  // him, so clear it rather than try to hold it. "Inside my own reach envelope"
  // is a natural definition of contested and needs no new constant.
  let punch = input !== null && (input.flags & InputFlags.GkPunch) !== 0;
  if (!punch && input === null && ball.py > PLAYER_CAPSULE_HEIGHT) {
    const reach = GK_DIVE_REACH * statMul(attrs.diving);
    for (let j = 0; j < MAX_PLAYERS; j++) {
      if (p.active[j] !== 1 || p.sentOff[j] === 1) continue;
      if (p.team[j] === p.team[id]!) continue;
      const ox = p.px[j]! - gx, oz = p.pz[j]! - gz;
      if (dsqrt(ox * ox + oz * oz) <= reach) { punch = true; break; }
    }
  }
  if (punch) {
    punchBall(state, id, attrs, goalX, forwardX, bounds);
    return true;
  }

  // ── Catch or parry? ──────────────────────────────────────────────────────
  // Two things decide it, and both are real:
  //   - pace on the shot, across §4.3's own 15–35 m/s envelope;
  //   - whether he is SET or at full stretch. A keeper travelling at dive speed
  //     is getting a hand to it, not gathering it.
  // The second term is why the spectacular saves spill and produce rebounds,
  // which is exactly what should happen.
  const stretch = diveSpeed > EPS ? clamp01(gkSpeed / diveSpeed) : 0;
  const chance =
    remapClamped(speed, SHOT_MAX_SPEED, SHOT_MIN_SPEED, 0, 1) *
    statMul(attrs.handling) *
    (1 - stretch);

  if (pcg32Float(state.rng) < chance) {
    p.gkHoldStartTick[id] = state.tick;
    p.possessing[id] = 1;
    ball.possessedBy = id;
    const smvx0 = ball.vx, smvy0 = ball.vy, smvz0 = ball.vz;
    ball.vx = 0; ball.vy = 0; ball.vz = 0;
    traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, smvx0, smvy0, smvz0);
    ball.wx = 0; ball.wy = 0; ball.wz = 0;
    ball.lastTouchedBy = id;
    ball.lastTouchedTeam = p.team[id]!;
    ball.lastTouchTick = state.tick;
    // A ball ON THE FLOOR is scooped, not caught standing. The claim reaches a
    // grounded ball on horizontal distance precisely because he BENDS for it
    // (see the reach note above), and until this clip existed he did that in
    // the standing catch pose — the visual half of that fix, finally paid.
    setAnim(p, id, ball.grounded === 1 ? AnimState.GkPickupGround : AnimState.GkCatch);
    p.actionTimer[id] = 0;
    p.vx[id] = 0; p.vy[id] = 0; p.vz[id] = 0;
    return true;
  }

  parryBall(state, id, attrs, gx, gy, gz, goalX, sideSign, bounds);
  return true;
}

/**
 * Parry. (§4.5: "out wide, not straight back to the striker")
 *
 * The target is the corner flag on the ball's side — palming it round the post
 * for a corner is what a good parry IS, and it is guaranteed wide because the
 * flag sits at halfWidth while the post sits at GOAL_WIDTH/2.
 * GK_PARRY_OUTWARD_BIAS blends that against the honest reflection off the
 * gloves, so at 0.75 physics still gets a quarter of a vote and the parries do
 * not all come out looking identical.
 */
function parryBall(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  gx: number, gy: number, gz: number,
  goalX: number,
  sideSign: number,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const ball = state.ball;

  const speed = dsqrt(ball.vx * ball.vx + ball.vy * ball.vy + ball.vz * ball.vz);

  // Honest bounce: reflect the incoming velocity about the glove normal.
  let nx = ball.px - gx, ny = ball.py - gy, nz = ball.pz - gz;
  const nl = dsqrt(nx * nx + ny * ny + nz * nz);
  if (nl > EPS) { nx /= nl; ny /= nl; nz /= nl; } else { nx = 0; ny = 1; nz = 0; }
  const vdotn = ball.vx * nx + ball.vy * ny + ball.vz * nz;
  let rx = ball.vx - 2 * vdotn * nx;
  let ry = ball.vy - 2 * vdotn * ny;
  let rz = ball.vz - 2 * vdotn * nz;
  const rl = dsqrt(rx * rx + ry * ry + rz * rz);
  if (rl > EPS) { rx /= rl; ry /= rl; rz /= rl; } else { rx = -sideSign; ry = 0.3; rz = 0; }

  // Away from the middle. A dead-centre ball has no natural side, so fall back
  // to the way he is already travelling, then to a seeded coin flip — never to a
  // fixed side, which would send every such parry the same way.
  let outZ = ball.pz !== 0 ? (ball.pz > 0 ? 1 : -1) : 0;
  if (outZ === 0) {
    outZ = p.vz[id]! !== 0 ? (p.vz[id]! > 0 ? 1 : -1) : (pcg32Float(state.rng) < 0.5 ? -1 : 1);
  }

  let cx = goalX - ball.px;
  let cy = -ball.py;
  let cz = outZ * bounds.halfWidth - ball.pz;
  const cl = dsqrt(cx * cx + cy * cy + cz * cz);
  if (cl > EPS) { cx /= cl; cy /= cl; cz /= cl; } else { cx = -sideSign; cy = 0; cz = outZ; }

  let dx = lerp(rx, cx, GK_PARRY_OUTWARD_BIAS);
  let dy = lerp(ry, cy, GK_PARRY_OUTWARD_BIAS);
  let dz = lerp(rz, cz, GK_PARRY_OUTWARD_BIAS);
  const dl = dsqrt(dx * dx + dy * dy + dz * dz);
  if (dl > EPS) { dx /= dl; dy /= dl; dz /= dl; } else { dx = cx; dy = cy; dz = cz; }

  // Last line of defence against the worst bug this feature can have: a parry
  // that goes in. Straight-line the deflection to the goal plane and, if it
  // would pass between the posts, throw the physics vote away and go fully wide.
  const dirIn = sideSign * dx;
  if (dirIn > EPS) {
    const tHit = (sideSign * (goalX - ball.px)) / dirIn;
    if (tHit > 0) {
      const hz = ball.pz + dz * tHit;
      const hy = ball.py + dy * tHit;
      if (Math.abs(hz) <= GOAL_WIDTH * 0.5 + BALL_RADIUS && hy <= GOAL_HEIGHT + BALL_RADIUS) {
        dx = cx; dy = cy; dz = cz;
      }
    }
  }

  // Better hands take the sting out of it, so a good keeper's spill lands closer
  // and is easier to gather. Poor hands squirt it away fast.
  const outSpeed = (speed * GK_PARRY_SPEED_RETENTION) / statMul(attrs.handling);
  const pyvx0 = ball.vx, pyvy0 = ball.vy, pyvz0 = ball.vz;
  ball.vx = dx * outSpeed;
  ball.vy = dy * outSpeed;
  ball.vz = dz * outSpeed;
  traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, pyvx0, pyvy0, pyvz0);
  ball.grounded = 0;
  ball.lastTouchedBy = id;
  ball.lastTouchedTeam = p.team[id]!;
  // Resets his own reaction clock — he must re-read the rebound before he can
  // dive at it. Which is precisely why rebounds are dangerous.
  ball.lastTouchTick = state.tick;

  // AnimState has no distinct parry pose: keep the dive if he is in one, else
  // GkPunch reads as the palm-away that it is.
  const anim = p.anim[id]!;
  if (anim !== AnimState.GkDiveLeft && anim !== AnimState.GkDiveRight) {
    setAnim(p, id, AnimState.GkPunch);
    p.actionTimer[id] = GK_DIVE_DURATION_S;
  }
}

/** Punch: a clearance, not a corner. Upfield and wide, with air under it. */
function punchBall(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  goalX: number,
  forwardX: number,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const ball = state.ball;

  const outZ = ball.pz !== 0 ? (ball.pz > 0 ? 1 : -1) : (pcg32Float(state.rng) < 0.5 ? -1 : 1);
  // Upfield and toward the touchline, on the same outward bias as the parry.
  const tx = goalX + forwardX * PENALTY_AREA_LENGTH;
  const tz = outZ * bounds.halfWidth;
  let dx = lerp(forwardX, tx - ball.px, GK_PARRY_OUTWARD_BIAS);
  let dz = lerp(0, tz - ball.pz, GK_PARRY_OUTWARD_BIAS);
  const dl = dsqrt(dx * dx + dz * dz);
  if (dl > EPS) { dx /= dl; dz /= dl; } else { dx = forwardX; dz = 0; }

  // A fist, not a boot: strength across the low end of the pass-speed envelope.
  const power = remapClamped(
    attrs.strength, ATTRIBUTE_MIN, ATTRIBUTE_MAX, GROUND_PASS_MIN_SPEED, DRIVEN_PASS_MIN_SPEED,
  );
  const theta = LOFTED_PASS_MAX_ANGLE_DEG * DEG2RAD;
  const vh = power * dcos(theta);
  const puvx0 = ball.vx, puvy0 = ball.vy, puvz0 = ball.vz;
  ball.vx = dx * vh;
  ball.vy = power * dsin(theta);
  ball.vz = dz * vh;
  traceBallSince(state, BallVelocityCause.GoalkeeperContact, id, puvx0, puvy0, puvz0);
  ball.grounded = 0;
  ball.lastTouchedBy = id;
  ball.lastTouchedTeam = p.team[id]!;
  ball.lastTouchTick = state.tick;
  ball.possessedBy = -1;
  p.possessing[id] = 0;
  p.gkHoldStartTick[id] = -1;
  setAnim(p, id, AnimState.GkPunch);
  p.actionTimer[id] = GK_DIVE_DURATION_S;
}

// ---------------------------------------------------------------------------
// Positioning (§4.5): "the bisector of the angle between the ball and the two
// posts, at a depth that shrinks the angle — recompute each tick"
// ---------------------------------------------------------------------------

/**
 * Where the keeper should be standing right now.
 *
 * The bisector half is the easy half: normalise ball→near-post and ball→far-post
 * and add them. The interesting half is the DEPTH, because "a depth that shrinks
 * the angle" has an exact answer rather than a magic fraction.
 *
 * Standing t metres along the bisector puts him t·sin(θ/2) from each post-ray,
 * where θ is the angle the goal subtends at the ball. Come out until that
 * equals his reach and the envelope covers both posts; come further and you gain
 * nothing while the chip gets easier. So:
 *
 *     t = GK_DIVE_REACH / sin(θ/2)
 *
 * That falls out of the geometry with no tunable at all, and it behaves: a ball
 * 10m out puts him ~3m off his line, a ball 6m out puts him ~1.4m off it, a ball
 * 30m out saturates against the 6-yard box. Which is where keepers actually
 * stand.
 *
 * Note there is not a single transcendental here — cos comes from a dot product
 * and sin from √(1-cos²), both correctly-rounded ops. This runs every tick for
 * every keeper and is exactly deterministic on every engine, for free.
 */
function computeBisectorTarget(
  bx: number, bz: number,
  goalX: number, sideSign: number,
  diveReach: number,
  bounds: PitchBounds,
): void {
  const halfGoal = GOAL_WIDTH * 0.5;
  const nearZ = -GOAL_AREA_WIDTH * 0.5;
  const farZ = GOAL_AREA_WIDTH * 0.5;

  // Ball → each post.
  let a1x = goalX - bx, a1z = -halfGoal - bz;
  let a2x = goalX - bx, a2z = halfGoal - bz;
  const l1 = dsqrt(a1x * a1x + a1z * a1z);
  const l2 = dsqrt(a2x * a2x + a2z * a2z);
  if (l1 < EPS || l2 < EPS) {
    // Ball on a post. Cover it and stop dividing by zero.
    standPos.x = goalX - sideSign * GK_MIN_DEPTH;
    standPos.z = clamp(bz, nearZ, farZ);
    return;
  }
  a1x /= l1; a1z /= l1; a2x /= l2; a2z /= l2;

  let dirX = a1x + a2x, dirZ = a1z + a2z;
  const dl = dsqrt(dirX * dirX + dirZ * dirZ);
  if (dl < EPS) {
    // Ball on the goal line between the posts: the two rays are opposed and the
    // bisector is undefined. He is on the line already.
    standPos.x = goalX - sideSign * GK_MIN_DEPTH;
    standPos.z = clamp(bz, nearZ, farZ);
    return;
  }
  dirX /= dl; dirZ /= dl;

  // Half-angle, from the dot product. No atan2 needed.
  const cosHalf = clamp(dirX * a1x + dirZ * a1z, -1, 1);
  const sinHalf = dsqrt(1 - cosHalf * cosHalf);

  const tIdeal = sinHalf > EPS ? diveReach / sinHalf : bounds.halfLength;
  const xIdeal = bx + dirX * tIdeal;
  // Depth measured from his goal line INTO the pitch.
  const depth = clamp(-sideSign * (xIdeal - goalX), GK_MIN_DEPTH, GK_MAX_DEPTH);
  const targetX = goalX - sideSign * depth;

  if (Math.abs(dirX) < EPS) {
    // Bisector runs parallel to the goal line — the ball is level with it, out by
    // the byline. Cover the near post.
    standPos.x = targetX;
    standPos.z = clamp(bz, -halfGoal, halfGoal);
    return;
  }

  // Solve the bisector for that depth directly rather than clamping along the
  // ray: t = (targetX - bx)/dirX.
  const t = (targetX - bx) / dirX;
  standPos.x = targetX;
  // He never wanders outside his 6-yard box laterally in normal angle-play.
  standPos.z = clamp(bz + dirZ * t, nearZ, farZ);
}

// ---------------------------------------------------------------------------
// AI keeper
// ---------------------------------------------------------------------------

/**
 * Sweeper-keeper (§4.5): "rush-out logic when a through-ball beats the last
 * defender". Gated on the role, or on danger being unambiguous.
 *
 * Danger here means the specific thing the brief describes: the ball is loose in
 * behind, inside GK_SWEEPER_TRIGGER_DISTANCE, and an opponent will get to it
 * before any of our defenders will. A SweeperKeeper does not need the opponent
 * to be favourite — sweeping up loose balls in behind is the role.
 */
function shouldSweep(
  state: WorldState,
  id: number,
  goalX: number,
  forwardX: number,
): boolean {
  const p = state.players;
  const ball = state.ball;
  const team = p.team[id]!;

  if (ball.possessedBy === id) return false;
  // Someone on our side already has it under control.
  if (ball.possessedBy >= 0 && p.team[ball.possessedBy] === team) return false;

  const gx = p.px[id]!, gz = p.pz[id]!;
  const bdx = ball.px - gx, bdz = ball.pz - gz;
  const gkToBall = dsqrt(bdx * bdx + bdz * bdz);
  if (gkToBall > GK_SWEEPER_TRIGGER_DISTANCE) return false;

  // Only in behind: between the keeper and everything else, not out in front.
  const ballDepth = forwardX * (ball.px - goalX);
  const gkDepth = forwardX * (gx - goalX);
  if (ballDepth < gkDepth) return false;

  let nearestOpp = Number.MAX_VALUE;
  let nearestMate = Number.MAX_VALUE;
  for (let j = 0; j < MAX_PLAYERS; j++) {
    if (p.active[j] !== 1 || p.sentOff[j] === 1 || j === id) continue;
    const dx = p.px[j]! - ball.px;
    const dz = p.pz[j]! - ball.pz;
    const d = dsqrt(dx * dx + dz * dz);
    if (p.team[j] === team) {
      if (d < nearestMate) nearestMate = d;
    } else if (d < nearestOpp) {
      nearestOpp = d;
    }
  }

  if (state.roles[id] === 'SweeperKeeper') {
    // He comes for anything he is favourite to reach — favourite over EVERYONE,
    // not just over the opposition.
    //
    // ⚠ `gkToBall < nearestMate` is not a nicety. Without it a SweeperKeeper
    // charges out for a ball one of his OWN defenders will reach first, which is a
    // liability rather than sweeping — and, measured (gk-distribution.test.ts),
    // it is how he ended up CHASING DOWN HIS OWN ROLL-OUT: released at 15.7 m/s
    // toward a free man 8 m away, he sprinted 7.5 m after it and re-armed the hold
    // 78 ticks later. That is the same family as the "keeper catches his own punt"
    // bug, and GK_OWN_TOUCH_REFRACTORY_S cannot fix it — the guard declines
    // HANDLING for 12 ticks, and the chase takes 78. The fix has to stop him
    // SETTING OFF.
    //
    // A spilled parry is untouched: resolveSave claims anything already inside
    // POSSESSION_RADIUS of his gloves regardless of this, and if he really is the
    // nearest man to the rebound this still lets him go and get it.
    return gkToBall < nearestOpp && gkToBall < nearestMate;
  }
  // Everyone else needs it to be a genuine emergency: the last defender has been
  // beaten and the keeper is the only one who can get there.
  return nearestOpp < nearestMate && gkToBall < nearestOpp;
}

function stepAiKeeper(
  state: WorldState,
  id: number,
  attrs: WorldState['attributes'][number],
  goalX: number,
  sideSign: number,
  forwardX: number,
  bounds: PitchBounds,
): void {
  const p = state.players;
  const ball = state.ball;

  const diveReach = GK_DIVE_REACH * statMul(attrs.diving);
  const diveSpeed = GK_DIVE_SPEED * statMul(attrs.diving);

  // Face the ball. Everything else about him follows from where the ball is.
  const fx = ball.px - p.px[id]!;
  const fz = ball.pz - p.pz[id]!;
  if (fx * fx + fz * fz > EPS) p.yaw[id] = datan2(fz, fx);

  // ── Dive? ────────────────────────────────────────────────────────────────
  // THE reaction gate. Not "how long since I noticed" — how long since the ball
  // was STRUCK. Before this elapses he does not move, and a shot that arrives
  // first simply beats him. That is the design, not a bug to tune away. (§4.5)
  const reactionTicks = gkReactionTicks(attrs.reflexes);
  const sinceTouch = state.tick - ball.lastTouchTick;
  if (ball.lastTouchTick >= 0 && sinceTouch >= reactionTicks && ball.possessedBy < 0) {
    // He only needs to look as far ahead as he could possibly act: any threat
    // beyond that will still be there next tick, when he re-decides.
    const horizonTicks = Math.ceil((GK_DIVE_REACH / GK_DIVE_SPEED) / FIXED_DT) + reactionTicks;
    predictThreat(
      state,
      p.px[id]!, p.py[id]! + GK_HAND_HEIGHT, p.pz[id]!,
      goalX, sideSign,
      diveReach, diveSpeed,
      horizonTicks,
    );
    if (threat.onTarget && threat.interceptable) {
      commitDive(state, id, threat.ix, threat.iy, threat.iz, sideSign, diveSpeed);
      return;
    }
  }

  // ── Sweep? ───────────────────────────────────────────────────────────────
  let targetX: number;
  let targetZ: number;
  let speedCap: number;
  if (shouldSweep(state, id, goalX, forwardX)) {
    // Lead the ball rather than chase it: aim where it will be when he arrives.
    const gx = p.px[id]!, gz = p.pz[id]!;
    const sprint = MAX_SPEED_SPRINT * statMul(attrs.pace);
    const dx = ball.px - gx, dz = ball.pz - gz;
    const eta = dsqrt(dx * dx + dz * dz) / Math.max(sprint, EPS);
    targetX = ball.px + ball.vx * eta;
    targetZ = ball.pz + ball.vz * eta;
    speedCap = sprint;
  } else {
    computeBisectorTarget(ball.px, ball.pz, goalX, sideSign, diveReach, bounds);
    targetX = standPos.x;
    targetZ = standPos.z;
    // Shuffling across is a jog; being caught out of position is a sprint.
    const dx = targetX - p.px[id]!, dz = targetZ - p.pz[id]!;
    const off = dsqrt(dx * dx + dz * dz);
    speedCap = off > diveReach
      ? MAX_SPEED_SPRINT * statMul(attrs.pace)
      : MAX_SPEED_JOG * statMul(attrs.gkPositioning);
  }

  moveToward(state, id, targetX, targetZ, speedCap);
  // AFTER the move, not before it. Both branches used to stamp the stance while
  // `targetX/targetZ` were still only an intention, so the clip was chosen from
  // a step that had not happened yet and `setStanceAnim` could not possibly know
  // whether he travelled. One call, downstream of the only mover.
  setStanceAnim(state, id);
}

/**
 * Walk him toward a spot at one physical step per tick, position-first.
 *
 * ⚠ It must NOT leave a residual velocity behind. The original left
 * `v = dir·speedCap` "for interpolation", on the belief that a null-input keeper's
 * velocity is harmlessly decelerated away at stage 4 before we run again. It is
 * not: stage-4 moveViaPhysics INTEGRATES that velocity into a second position
 * move every tick, so the keeper covered ~2× speedCap and repositioned at a
 * superhuman ~17 m/s — the "keeper snaps rather than moves / materialises on the
 * ball" the orchestrator probe saw. Writing the capped step and then ZEROING the
 * velocity makes this the sole mover: exactly speedCap, one integration, the same
 * discipline as an outfield player. (§4.5 positioning, §10.1 never-superhuman)
 *
 * A bonus falls out of it: a set keeper who reaches the ball while merely
 * shuffling now has zero speed, so resolveSave() reads stretch = 0 and he GATHERS
 * cleanly instead of spilling a ball he was well set for.
 */
function moveToward(state: WorldState, id: number, tx: number, tz: number, speedCap: number): void {
  const p = state.players;
  const dx = tx - p.px[id]!;
  const dz = tz - p.pz[id]!;
  const d = dsqrt(dx * dx + dz * dz);
  const stepLen = speedCap * FIXED_DT;

  const beforeX = p.px[id]!;
  const beforeZ = p.pz[id]!;
  if (d <= stepLen || d < EPS) {
    p.px[id] = tx;
    p.pz[id] = tz;
  } else {
    const s = stepLen / d;
    p.px[id] = p.px[id]! + dx * s;
    p.pz[id] = p.pz[id]! + dz * s;
  }
  // Tell the clip chooser what the body actually did. The velocity below is
  // about to be zeroed, which is correct for the physics and a lie to every
  // reader that picks an animation from |v| — see gk-travel.ts for the measured
  // cost of that lie (45.7 m slid under a planted foot per three minutes).
  recordGkTravel(id, p.px[id]! - beforeX, p.pz[id]! - beforeZ);
  // Zero velocity so stage-4 moveViaPhysics does not double-move him — the capped
  // position write above IS the movement.
  p.vx[id] = 0;
  p.vz[id] = 0;
  p.py[id] = 0;
  p.vy[id] = 0;
}

// ---------------------------------------------------------------------------
// Human keeper (§4.5): "a real player can take GK in any mode. Give them a
// distinct scheme (dive = mouse direction + click, rush out = Shift, punch/catch
// = modifier)."
// ---------------------------------------------------------------------------

/**
 * Only the keeper-specific verbs live here. Ordinary locomotion already ran in
 * player-controller.ts at stage 4 — a human keeper walks like anyone else, and
 * duplicating that would double-integrate him.
 *
 * NOTE there is no reaction gate on a human dive, and that is deliberate: the
 * 80–220ms delay models a keeper's biology, and a human already has one. Charging
 * them twice for it would make human keepers strictly worse than AI ones and
 * invert §10.1's guarantee that AI can never be superhuman.
 */
function stepHumanKeeper(
  state: WorldState,
  id: number,
  input: InputCommand,
  attrs: WorldState['attributes'][number],
  sideSign: number,
): void {
  const p = state.players;
  const ball = state.ball;
  // ── THE KEEPER'S OWN CONTROLS (owner, 2026-08-15: "make his controls a
  //    little bit different from other players cause he can dive left right
  //    and stop the ball with legs and hands") ──────────────────────────────
  //
  // `InputFlags.GkDive` and `GkRush` have existed in the sim since the keeper
  // landed and NO CLIENT INPUT HAS EVER SET THEM - which is most of "the goal
  // keeper is acting dumb": a human keeper had a striker's verbs and no way to
  // dive at all. Rather than burn two more keybinds, his EMPTY HANDS re-use
  // the strike buttons, which a keeper without the ball cannot use anyway:
  //
  //   ball in his gloves  -> shoot/pass mean PUNT and THROW, unchanged.
  //   ball not his        -> shoot means DIVE along the aim (left, right, or
  //                          forward - the aim decides, so one button covers
  //                          both sides), pass means RUSH OUT to claim it.
  //
  // The AI keeper is untouched: it never sends input.
  const holding = p.gkHoldStartTick[id]! >= 0;
  let flags = input.flags;
  // ── DEDICATED KEEPER KEYS (owner, 2026-08-16) ────────────────────────────
  //
  //   C  dive LEFT     V  dive RIGHT     X  claim / hold the ball
  //
  // These override the aim for direction, which is the whole reason they are
  // worth having: a keeper dives ACROSS his goal while facing the shot, so his
  // aim is where he is looking and not where he is going. The mouse-aim dive
  // below stays as the third option (aim + click), because on a shot straight
  // at him "dive where I am looking" is exactly right.
  //
  // ⚠ The flags ride keys that already mean something to an outfield player
  // (radar / camera / call-for-pass). That is safe because this function runs
  // for the GK slot only — see the InputManager note.
  let diveSide = 0;
  if (!holding) {
    if ((flags & InputFlags.GkDiveLeft) !== 0) { flags |= InputFlags.GkDive; diveSide = -1; }
    else if ((flags & InputFlags.GkDiveRight) !== 0) { flags |= InputFlags.GkDive; diveSide = 1; }
    if ((flags & InputFlags.Shoot) !== 0) flags |= InputFlags.GkDive;
    if ((flags & InputFlags.Pass) !== 0) flags |= InputFlags.GkRush;
    // X — the DELIBERATE claim. A save on a struck shot stays automatic
    // (resolveSave's own roll): no human reacts inside that window, and
    // requiring a keypress for it would simply mean conceding. This is for the
    // ball he chooses to pick up — a rolling back-pass, a loose ball, a cross
    // he has come for.
    if ((flags & InputFlags.GkHold) !== 0) flags |= InputFlags.GkRush;
  }

  // ── Dive: mouse direction + click ────────────────────────────────────────
  if ((flags & InputFlags.GkDive) !== 0) {
    const diveSpeed = GK_DIVE_SPEED * statMul(attrs.diving);
    // C/V dive ACROSS the goal line, perpendicular to the way he is facing the
    // shot. `sideSign` points from the centre spot toward his own goal, so the
    // across-goal axis is its perpendicular; diveSide picks which way.
    let dirX = yawForwardX(input.aimYaw);
    let dirZ = yawForwardZ(input.aimYaw);
    if (diveSide !== 0) {
      dirX = 0;
      dirZ = diveSide;
    }
    // Airborne for the dive duration, so the arc reads as a dive rather than a
    // slide: v = ½·g·T. The wheel's lift bias then trades height for length,
    // which gives a human keeper a low smother and a full-stretch high dive off
    // one verb. (§4.1)
    const vy = 0.5 * G * GK_DIVE_DURATION_S * (1 + clamp(input.liftBias, -1, 1));
    p.vx[id] = dirX * diveSpeed;
    p.vz[id] = dirZ * diveSpeed;
    p.vy[id] = Math.max(vy, 0);
    p.actionTimer[id] = GK_DIVE_DURATION_S;
    // A sideways dive keeps him FACING the shot; only an aimed dive turns him.
    if (diveSide === 0) p.yaw[id] = input.aimYaw;

    const forwardX = -sideSign;
    setAnim(p, id, dirZ * forwardX >= 0 ? AnimState.GkDiveRight : AnimState.GkDiveLeft);
    return;
  }

  // ── Rush out: charge the ball ────────────────────────────────────────────
  // A distinct verb from Sprint, which player-controller.ts already handles:
  // this is the one-button "come and claim it" that makes keeping playable
  // without asking someone to hand-steer a sweep at 8.5 m/s.
  if ((flags & InputFlags.GkRush) !== 0) {
    const sprint = MAX_SPEED_SPRINT * statMul(attrs.pace);
    const dx = ball.px - p.px[id]!;
    const dz = ball.pz - p.pz[id]!;
    const eta = dsqrt(dx * dx + dz * dz) / Math.max(sprint, EPS);
    moveToward(state, id, ball.px + ball.vx * eta, ball.pz + ball.vz * eta, sprint);
    if (dx * dx + dz * dz > EPS) p.yaw[id] = datan2(dz, dx);
    setStanceAnim(state, id);
    return;
  }

  // Otherwise he stands where the human put him. resolveSave() already ran, so
  // anything inside his reach has been dealt with; GkPunch is read there.
  setStanceAnim(state, id);
}
